import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UploadOutlined,
  CloseOutlined,
  StarFilled,
  PhoneOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import Select from 'react-select';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import { useLocationMaster } from '../../../hooks/useLocationMaster';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import '../../../assets/styles/submit_quotation_to_buyer.scss';
// Warranty period units map
const WARRANTY_PERIOD_UNITS = {
  'MONTHS': 'months',
  'YEARS': 'years',
  'DAYS': 'days',
  'WEEKS': 'weeks'
};

// Delivery time units map
const DELIVERY_TIME_UNITS = {
  'WEEKS': 'weeks',
  'DAYS': 'days',
  'MONTHS': 'months'
};




// Function to generate Quotation ID
// Format: QT-YYYY-XXXXXX (e.g., QT-2025-001234)
// Where YYYY is the current year and XXXXXX is a 6-digit unique identifier
const generateQuotationId = () => {
  const now = new Date();
  const year = now.getFullYear();
  
  // Generate a unique 6-digit number from timestamp
  // Use the last 6 digits of the timestamp (milliseconds since epoch)
  // This ensures uniqueness while keeping the format readable
  const timestamp = now.getTime();
  const sequence = String(timestamp).slice(-6).padStart(6, '0');
  
  return `QT-${year}-${sequence}`;
};

// Quote Status options for react-select
const QUOTE_STATUS_OPTIONS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Rejected', label: 'Rejected' }
];

const parseStoredJson = (value, fallback) => {
  if (value == null || value === '') return fallback;
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const formatStoredDate = (value) => {
  if (!value) return new Date().toISOString().split('T')[0];
  try {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? new Date().toISOString().split('T')[0] : date.toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

const mapStoredQuotationToForm = (stored) => {
  const paymentTermsParsed = parseStoredJson(stored.payment_terms, []);
  const attachments = parseStoredJson(stored.attachments, []);

  const items = (stored.items || []).map((item, index) => {
    const bulkPricing = parseStoredJson(item.bulk_pricing, []);
    const certifications = parseStoredJson(item.certifications, []);

    return {
      id: item.line_item_id || index + 1,
      description: item.description || '',
      quantity: parseFloat(item.quantity) || 0,
      uom: item.uom != null ? String(item.uom) : '',
      unitPrice: parseFloat(item.unit_price) || 0,
      tax: item.tax != null && item.tax !== '' ? item.tax : '',
      discount: parseFloat(item.discount) || 0,
      igst: parseFloat(item.igst) || 0,
      cess: parseFloat(item.cess) || 0,
      sgst: parseFloat(item.sgst) || 0,
      customs: parseFloat(item.customs) || 0,
      subtotal: parseFloat(item.subtotal) || 0,
      discountAmount: parseFloat(item.discount_amount) || 0,
      fobValue: parseFloat(item.fob_value) || 0,
      igstAmount: parseFloat(item.igst_amount) || 0,
      cessAmount: parseFloat(item.cess_amount) || 0,
      sgstAmount: parseFloat(item.sgst_amount) || 0,
      customsAmount: parseFloat(item.customs_amount) || 0,
      freightAmount: parseFloat(item.freight_amount) || 0,
      insuranceAmount: parseFloat(item.insurance_amount) || 0,
      handlingAmount: parseFloat(item.handling_amount) || 0,
      cifValue: parseFloat(item.cif_value) || 0,
      bcdAmount: parseFloat(item.bcd_amount) || 0,
      swsAmount: parseFloat(item.sws_amount) || 0,
      assessableValue: parseFloat(item.assessable_value) || 0,
      taxAmt: parseFloat(item.tax_amount) || 0,
      total: parseFloat(item.total) || 0,
      hsnCode: item.hsn_code || '',
      countryOfOrigin: item.country_of_origin || '',
      warranty: item.warranty_period || '',
      certifications: Array.isArray(certifications) ? certifications : [],
      sampleOffered: item.sample_offered || '',
      sampleType: item.sample_type || '',
      sampleQuantity: parseFloat(item.sample_quantity) || 0,
      sampleUnit: item.sample_unit != null ? String(item.sample_unit) : '',
      sampleCost: parseFloat(item.sample_cost) || 0,
      dispatchLeadTime: item.dispatch_lead_time || '',
      returnPolicy: item.return_policy || '',
      bulkPricing: Array.isArray(bulkPricing) ? bulkPricing : [],
    };
  });

  return {
    quotationId: stored.quotation_number || generateQuotationId(),
    quoteStatus: stored.quote_status || 'Draft',
    quoteDate: formatStoredDate(stored.quote_date),
    validityDays: stored.validity_days != null ? String(stored.validity_days) : '30',
    tradeType: stored.trade_type || '',
    exportTaxType: stored.export_tax_type || '',
    igstRegistrationNumber: stored.igst_registration_number || '',
    arnNumber: stored.arn_number || '',
    items,
    paymentTerms: Array.isArray(paymentTermsParsed) && paymentTermsParsed.length > 0
      ? paymentTermsParsed
      : [
          { id: 1, label: '', value: '', placeholder: 'e.g., 30 days from invoice date' },
          { id: 2, label: 'Advance payment:', value: '', placeholder: 'e.g., 30% with PO' },
          { id: 3, label: 'Balance', value: '', placeholder: 'e.g., 70% before dispatch' },
        ],
    paymentMethod: stored.payment_method || '',
    currency: stored.currency || '',
    tradeTerms: stored.trade_terms || '',
    paymentDueDays: stored.payment_due_days != null ? String(stored.payment_due_days) : '',
    advancePayment: stored.advance_payment || '',
    balancePayment: stored.balance_payment || '',
    shippingAddress: stored.shipping_address || '',
    shippingCountry: stored.shipping_country || '',
    shippingState: stored.shipping_state || '',
    shippingCity: stored.shipping_city || '',
    shippingPincode: stored.shipping_pincode || '',
    shippingMethod: stored.shipping_method || '',
    sellerShippingMode: stored.seller_shipping_mode || 'SELF_SHIPPING',
    destinationPort: stored.destination_port || '',
    leadTimeDays: stored.lead_time_days != null ? String(stored.lead_time_days) : '',
    deliveryTerms: {
      deliveryTime: stored.delivery_time || '',
      incoterms: stored.incoterms || '',
      warranty: stored.warranty || '',
    },
    freightCharges: stored.freight_charges != null ? String(stored.freight_charges) : '',
    insuranceCharges: stored.insurance_charges != null ? String(stored.insurance_charges) : '',
    handlingClearance: stored.handling_clearance != null ? String(stored.handling_clearance) : '',
    declarationAccepted: Boolean(stored.declaration_accepted),
    existingAttachments: Array.isArray(attachments) ? attachments : [],
  };
};

export default function SubmitQuotationToBuyer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { countries: rawCountries, states: rawStates, updateListsForCountry } = useLocationMaster();
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  const { shippingTermsOptions, loading: incotermLoading } = useFactoryDetailsMaster();
  const { attributeUnitOptions, loading: uomLoading, attributeUnit } = useProductDetailsMaster();
  
  // Debug: Log UOM options
  useEffect(() => {
    console.log('UOM Debug - Loading:', uomLoading, 'Options Count:', attributeUnitOptions?.length || 0, 'Raw Data:', attributeUnit);
    if (attributeUnitOptions && attributeUnitOptions.length > 0) {
      console.log('UOM Options loaded:', attributeUnitOptions);
    } else if (!uomLoading) {
      console.warn('UOM Options not loaded or empty. Loading:', uomLoading, 'Options:', attributeUnitOptions, 'Raw:', attributeUnit);
    }
  }, [attributeUnitOptions, uomLoading, attributeUnit]);

  // Update UOM values in line items when attributeUnitOptions becomes available
  // This ensures RFQ unit values are properly mapped to dropdown IDs
  // Uses original RFQ unit value from rfqData if available, otherwise uses current line item UOM
  useEffect(() => {
    if (!uomLoading && attributeUnitOptions && attributeUnitOptions.length > 0 && quotation.items && quotation.items.length > 0) {
      // Get original RFQ unit value if available (from rfqData) - this is the ID from the database
      const originalRFQUnit = rfqData?.items?.[0]?.uom || null;
      console.log('UOM Re-mapping useEffect triggered:', {
        originalRFQUnit,
        lineItemsCount: quotation.items.length,
        firstItemUOM: quotation.items[0]?.uom,
        optionsCount: attributeUnitOptions.length
      });
      
      // Check if any line item has a UOM that needs to be mapped to ID
      const needsUpdate = quotation.items.some(item => {
        if (!item.uom) return false;
        // Check if the current UOM value is a label (not an ID)
        // If it doesn't match any option value (ID), it might be a label
        const foundById = attributeUnitOptions.find(opt => 
          String(opt.value) === String(item.uom) || 
          Number(opt.value) === Number(item.uom)
        );
        const foundByLabel = attributeUnitOptions.find(opt => opt.label === String(item.uom));
        // Needs update if it's a label (foundByLabel but not foundById)
        return foundByLabel && !foundById;
      });

      if (needsUpdate) {
        console.log('Updating UOM values in line items to IDs...');
        setQuotation(prev => {
          const updatedLineItems = prev.items.map((item, index) => {
            if (!item.uom) return item;
            
            // Use original RFQ unit value if available (for first line item), otherwise use current UOM
            const sourceUnit = (index === 0 && originalRFQUnit) ? originalRFQUnit : item.uom;
            
            // Re-map the UOM value to ID
            const mappedUOMId = mapRFQUnitToUOMId(sourceUnit);
            if (mappedUOMId && mappedUOMId !== item.uom) {
              console.log(`Line Item ${index + 1}: Updating UOM from "${item.uom}" to ID "${mappedUOMId}" (source: "${sourceUnit}")`);
              return { ...item, uom: mappedUOMId };
            }
            return item;
          });
          return { ...prev, items: updatedLineItems };
        });
      } else {
        console.log('UOM values are already correctly mapped to IDs');
      }
    }
    // Only depend on attributeUnitOptions and uomLoading to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeUnitOptions, uomLoading]);
  
  // Update currency value when currencyOptions becomes available
  // This ensures RFQ currency values are properly mapped to dropdown values
  useEffect(() => {
    if (!currencyLoading && currencyOptions && currencyOptions.length > 0) {
      // Only update if currency is not already set or doesn't match any option
      if (quotation.currency && !findCurrencyOption(quotation.currency)) {
        // Currency value doesn't match any option, try to find it
        // This handles the case where RFQ loaded before currency options were available
        console.log('Currency value not found in options, attempting to re-map:', quotation.currency);
        // The currency should have been set from RFQ, but if options weren't loaded, it might need re-mapping
        // This is handled in the RFQ loading code, so we just log here
      }
      
      // Update targetPriceRange in rfqData if currency options are now available
      if (rfqData?.items?.[0]) {
        const item = rfqData.items[0];
        const currentRange = item.targetPriceRange || '';
        
        // Check if targetPriceRange needs currency symbol update
        // If it doesn't have a currency symbol, re-format it using original values
        const needsReformat = !currentRange.match(/^[₹$€£]/) && (item._originalPriceMin || item._originalPriceMax);
        
        if (needsReformat && item._originalCurrency) {
          // Re-format the price range with currency options now available
          const formatPriceRange = (min, max, currency) => {
            if (!min && !max) return '';
            
            // Map currency ID to currency code if needed
            let currencyCode = currency || 'INR';
            if (currency && currencyOptions && currencyOptions.length > 0) {
              const currencyOption = findCurrencyOption(currency);
              if (currencyOption) {
                // Use currency code from the option
                currencyCode = currencyOption.currencyCode || currencyOption.value || currency;
              } else {
                // If not found, use the currency value as-is (might already be a code)
                currencyCode = currency;
              }
            }
            
            // Get currency symbol
            const symbol = currencyCode === 'INR' ? '₹' : currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '€' : currencyCode === 'GBP' ? '£' : currencyCode;
            
            if (min && max) {
              return `${symbol}${min} - ${symbol}${max} per unit`;
            } else if (min) {
              return `${symbol}${min} per unit (min)`;
            } else if (max) {
              return `${symbol}${max} per unit (max)`;
            }
            return '';
          };
          
          const newPriceRange = formatPriceRange(item._originalPriceMin, item._originalPriceMax, item._originalCurrency);
          if (newPriceRange && newPriceRange !== currentRange) {
            console.log('Re-formatting targetPriceRange:', currentRange, '->', newPriceRange);
            setRfqData(prev => ({
              ...prev,
              items: prev.items.map((it, idx) => 
                idx === 0 ? { ...it, targetPriceRange: newPriceRange } : it
              )
            }));
          }
        }
      }
    }
    // Only depend on currencyOptions and currencyLoading to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyOptions, currencyLoading]);

  // Update UOM in rfqData.items when attributeUnitOptions becomes available
  // This ensures the UOM label is displayed correctly in "Items Requested by Buyer" section
  useEffect(() => {
    if (!uomLoading && attributeUnitOptions && attributeUnitOptions.length > 0 && rfqData?.items?.[0]) {
      const currentUOM = rfqData.items[0].uom;
      // Check if current UOM is an ID (numeric) or doesn't match any option label
      const isNumeric = !isNaN(currentUOM) && currentUOM !== '';
      const foundOption = attributeUnitOptions.find(opt => opt.label === String(currentUOM));
      
      if (isNumeric || !foundOption) {
        // UOM is an ID or not found in options, map it to label for display
        const mappedUOMLabel = getUOMLabelFromId(currentUOM);
        if (mappedUOMLabel && mappedUOMLabel !== currentUOM) {
          console.log('Updating UOM in rfqData.items from', currentUOM, 'to label', mappedUOMLabel);
          setRfqData(prev => ({
            ...prev,
            items: prev.items.map((item, idx) => 
              idx === 0 ? { ...item, uom: mappedUOMLabel } : item
            )
          }));
        }
      }
    }
    // Only depend on attributeUnitOptions and uomLoading to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeUnitOptions, uomLoading]);

  // Map countries to country of origin options (using country name/label as both value and label)
  const countryOfOriginOptions = rawCountries.map((country) => ({
    value: country.label || country.country || country.country_name || '',
    label: country.label || country.country || country.country_name || ''
  })).filter(opt => opt.value && opt.label);
  
  // Helper function to find currency option by value (supports both currency code and ID)
  const findCurrencyOption = (value) => {
    if (!value || !currencyOptions || currencyOptions.length === 0) return null;
    // Try to find by value (currency code or ID)
    return currencyOptions.find((opt) => 
      String(opt.value) === String(value) || 
      String(opt.currencyCode) === String(value) ||
      opt.value === value
    ) || null;
  };

  // Helper function to get currency symbol from selected currency ID
  const getCurrencySymbol = () => {
    if (!quotation.currency || !currencyOptions || currencyOptions.length === 0) {
      return '₹'; // Default to INR symbol
    }
    const currencyOption = findCurrencyOption(quotation.currency);
    if (currencyOption && currencyOption.currencySymbol) {
      return currencyOption.currencySymbol;
    }
    // Fallback: try to get symbol from currency code
    if (currencyOption && currencyOption.currencyCode) {
      const code = String(currencyOption.currencyCode).toUpperCase();
      const symbolMap = {
        'INR': '₹',
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'AUD': 'A$',
        'CAD': 'C$'
      };
      return symbolMap[code] || code;
    }
    return '₹'; // Default fallback
  };

  // Helper function to format currency value with symbol
  const formatCurrency = (amount) => {
    const symbol = getCurrencySymbol();
    if (amount === null || amount === undefined || amount === '') return '-';
    const formatted = parseFloat(amount).toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    return `${symbol} ${formatted}`;
  };

  // Helper function to map RFQ unit value to UOM ID (for backend storage)
  // RFQ might store unit as ID or label, but we need to store ID in quotation
  const mapRFQUnitToUOMId = (rfqUnitValue) => {
    if (!rfqUnitValue) return '';
    
    // Convert to string for comparison
    const unitValueStr = String(rfqUnitValue).trim();
    if (!unitValueStr) return '';
    
    // If attributeUnitOptions is not loaded yet, return the value as is
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) {
      console.log('mapRFQUnitToUOMId: Options not loaded yet, returning value as-is:', unitValueStr);
      return unitValueStr;
    }
    
    // First try to find by value (ID) - in case RFQ stored the ID (most common case)
    // Convert to number for comparison if it's a numeric string
    const unitValueNum = isNaN(unitValueStr) ? null : Number(unitValueStr);
    const foundByValue = attributeUnitOptions.find(opt => {
      // Try exact string match first
      if (String(opt.value) === unitValueStr) return true;
      // Try numeric match if both are numbers
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    if (foundByValue) {
      console.log('mapRFQUnitToUOMId: Found by ID/value', unitValueStr, '->', foundByValue.value);
      return String(foundByValue.value);
    }
    
    // Then try to find by label (exact match) - if RFQ stored label, convert to ID
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === unitValueStr || 
      opt.label?.toLowerCase() === unitValueStr.toLowerCase()
    );
    if (foundByLabel) {
      console.log('mapRFQUnitToUOMId: Found by label', unitValueStr, '->', foundByLabel.value);
      return String(foundByLabel.value);
    }
    
    // If not found, log warning and return the value as is
    console.warn('mapRFQUnitToUOMId: No match found for', unitValueStr, 'Available options:', attributeUnitOptions.length);
    return unitValueStr;
  };

  // Helper function to get UOM label from ID (for display purposes)
  const getUOMLabelFromId = (uomId) => {
    if (!uomId) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return String(uomId);
    
    const found = attributeUnitOptions.find(opt => 
      String(opt.value) === String(uomId) || 
      Number(opt.value) === Number(uomId)
    );
    return found ? found.label : String(uomId);
  };
  
  // RFQ Data (Read-only) - will be populated from backend
  const [rfqData, setRfqData] = useState({
    rfqId: '',
    rfqDate: '',
    rfqStatus: '',
    requiredByDate: '',
    paymentTerms: '',
    tradeTerms: '',
    shippingAddress: '',
    shippingCountry: '',
    shippingState: '',
    shippingCity: '',
    shippingPincode: '',
    shippingMethod: '',
    sellerShippingMode: 'SELF_SHIPPING',
    destinationPort: '',
    leadTimeDays: '',
    buyer: {
      companyName: '',
      contactPerson: '',
      rating: 0,
      email: '',
      contactNumber: '',
      deliveryAddress: '',
      deliveryCity: '',
      deliveryState: '',
      deliveryCountry: '',
      deliveryPincode: ''
    },
    items: [],
    specialRequirements: ''
  });

  const [loadingRFQ, setLoadingRFQ] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [rfqId, setRfqId] = useState(null); // Store numeric RFQ ID for submission
  const [quotationDbId, setQuotationDbId] = useState(null); // Store database quotation_id for conversations
  const [existingAttachments, setExistingAttachments] = useState([]);
  const isEditingDraft = Boolean(
    location?.state?.editMode || new URLSearchParams(location.search).get('edit') === 'true'
  );
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Seller location data (from seller's company profile)
  const [sellerLocation, setSellerLocation] = useState({
    country: null,
    state: null,
    countryName: '',
    stateName: ''
  });

  // Helper function to get country ID from label/name or return ID if already an ID
  // Data sources:
  // - Seller country: company_profile.address.country (stored as label/name, e.g., "India") - convert to ID
  // - Buyer country: rfq_requests.shipping_country (stored as name/string, e.g., "India") - convert to ID
  // - Countries master: useLocationMaster maps { country_id, country } to { id, label }
  const getCountryId = (countryIdOrName) => {
    if (!countryIdOrName) return null;
    const countryStr = String(countryIdOrName).trim();
    
    // If it's already an ID (number), return it
    // This handles backward compatibility with old ID-based data
    if (!isNaN(countryStr) && !countryStr.includes(' ')) {
      return Number(countryStr);
    }
    
    // If it's a name (label), look it up in countries master to get the ID
    // Seller country from company_profile.address.country is a label that needs to be converted to ID
    // Countries from master data are mapped to: { id: country_id, label: country }
    if (rawCountries && rawCountries.length > 0) {
      const country = rawCountries.find(c => {
        // Match against 'label' field (which is mapped from 'country' field in database)
        const countryLabel = (c.label || c.country_name || c.country || '').toLowerCase().trim();
        const inputLabel = countryStr.toLowerCase().trim();
        return countryLabel === inputLabel;
      });
      
      if (country) {
        // Return the ID (which is mapped from country_id in database)
        const countryId = country.id || country.country_id || country.value;
        return countryId ? Number(countryId) : null;
      }
    }
    
    // If lookup failed, return null
    return null;
  };

  // Keep getCountryName for other uses (display purposes)
  const getCountryName = (countryIdOrName) => {
    if (!countryIdOrName) return '';
    const countryStr = String(countryIdOrName).trim();
    
    // If it's already a name (contains letters), return as is
    if (isNaN(countryStr)) {
      return countryStr;
    }
    
    // If it's an ID (number), look it up in countries master
    if (rawCountries && rawCountries.length > 0) {
      const country = rawCountries.find(c => {
        const countryId = c.id || c.country_id || c.value;
        if (countryId !== undefined && countryId !== null) {
          if (String(countryId) === countryStr) return true;
          if (Number(countryId) === Number(countryStr)) return true;
        }
        return false;
      });
      
      if (country) {
        return country.label || country.country_name || country.country || '';
      }
    }
    
    return countryStr;
  };

  // Helper function to get state ID from label/name or return ID if already an ID
  // Data sources:
  // - Seller state: company_profile.address.state (stored as label/name, e.g., "Tamil Nadu") - convert to ID
  // - Buyer state: rfq_requests.shipping_state (stored as name/string, e.g., "Tamil Nadu") - convert to ID
  // - States master: useLocationMaster maps { state_id, state } to { id, label }
  const getStateId = (stateIdOrName) => {
    if (!stateIdOrName) return null;
    const stateStr = String(stateIdOrName).trim();
    
    // If it's already an ID (number), return it
    // This handles backward compatibility with old ID-based data
    if (!isNaN(stateStr) && !stateStr.includes(' ')) {
      return Number(stateStr);
    }
    
    // If it's a name (label), look it up in states master to get the ID
    // Seller state from company_profile.address.state is a label that needs to be converted to ID
    // States from master data are mapped to: { id: state_id, label: state }
    if (rawStates && rawStates.length > 0) {
      const inputLabel = stateStr.toLowerCase().trim();
      
      // Try exact match first
      let state = rawStates.find(s => {
        const stateLabel = (s.label || s.state_name || s.state || s.name || '').toLowerCase().trim();
        return stateLabel === inputLabel;
      });
      
      // If exact match failed, try partial match (handles variations)
      if (!state) {
        state = rawStates.find(s => {
          const stateLabel = (s.label || s.state_name || s.state || s.name || '').toLowerCase().trim();
          // Try removing spaces for comparison (e.g., "Tamil Nadu" vs "TamilNadu")
          const normalizedStateLabel = stateLabel.replace(/\s+/g, '');
          const normalizedInputLabel = inputLabel.replace(/\s+/g, '');
          return normalizedStateLabel === normalizedInputLabel ||
                 stateLabel.includes(inputLabel) ||
                 inputLabel.includes(stateLabel);
        });
      }
      
      if (state) {
        // Return the ID (which is mapped from state_id in database)
        const stateId = state.id || state.state_id || state.value;
        return stateId ? Number(stateId) : null;
      }
    }
    
    // If lookup failed, return null
    return null;
  };

  // Function to determine trade type based on buyer and seller locations
  // Both buyer (from RFQ) and seller (from profile) locations are stored as labels (strings)
  // Directly compare labels instead of converting to IDs
  const determineTradeType = (buyerCountry, buyerState, sellerCountry, sellerState) => {
    // Both buyer and seller locations are labels (strings) - compare directly
    // Buyer: RFQ shipping_country/shipping_state are labels (strings)
    // Seller: Profile address.country/address.state are labels (strings)
    
    // Helper function to normalize strings for comparison
    const normalize = (str) => {
      if (!str) return '';
      return String(str).toLowerCase().trim();
    };
    
    // Check if location is India (by name/label)
    const isIndia = (countryLabel) => {
      if (!countryLabel) return false;
      const normalized = normalize(countryLabel);
      return normalized === 'india' || normalized.includes('india');
    };

    const buyerCountryLabel = normalize(buyerCountry);
    const sellerCountryLabel = normalize(sellerCountry);

    const buyerInIndia = isIndia(buyerCountry);
    const sellerInIndia = isIndia(sellerCountry);

    // Both in India
    if (buyerInIndia && sellerInIndia) {
      // Compare state labels directly (case-insensitive)
      const buyerStateLabel = normalize(buyerState);
      const sellerStateLabel = normalize(sellerState);
      
      // If both states are provided, compare them
      if (buyerStateLabel && sellerStateLabel) {
        if (buyerStateLabel === sellerStateLabel) {
          return 'IN India to India - Intra-State (Same State)';
        } else {
          return 'IN India to India - Inter-State (Different States)';
        }
      }
      
      // If one or both states are missing, default to Inter-State
      return 'IN India to India - Inter-State (Different States)';
    }
    
    // Buyer in India, Seller outside → Import
    if (buyerInIndia && !sellerInIndia) {
      return '🌎 →IN Import into India (Foreign → India)';
    }
    
    // Buyer outside, Seller in India → Export
    if (!buyerInIndia && sellerInIndia) {
      return 'IN → 🌎 Export from India (India → Foreign)';
    }
    
    // Both outside India → Third Country Trade
    return '🌎 Third Country Trade (Foreign → Foreign)';
  };

  // Quotation Data (Editable)
  const [quotation, setQuotation] = useState({
    quotationId: generateQuotationId(),
    quoteStatus: 'Draft', // Default to 'Draft' for new quotations
    quoteDate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
    validityDays: '30', // Default to 30 days validity
    tradeType: 'IN → 🌎 Export from India (India → Foreign)',
    exportTaxType: '',
    igstRegistrationNumber: '',
    arnNumber: '',
    items: [
      {
        id: 1,
        description: 'Industrial Grade Electrical Components',
        quantity: 100,
        uom: 'pcs',
        unitPrice: 1200,
        tax: 18,
        discount: 0,
        igst: 0,
        cess: 0,
        sgst: 0,
        customs: 0,
        subtotal: 120000,
        discountAmount: 0,
        fobValue: 120000,
        igstAmount: 0,
        cessAmount: 0,
        sgstAmount: 0,
        customsAmount: 0,
        freightAmount: 0,
        insuranceAmount: 0,
        handlingAmount: 0,
        cifValue: 0,
        bcdAmount: 0,
        swsAmount: 0,
        assessableValue: 0,
        taxAmt: 22500,
        total: 120000,
        hsnCode: '8517',
        countryOfOrigin: 'India',
        warranty: '12 months',
        certifications: ['ISO 9001', 'BIS'],
        sampleOffered: 'Yes',
        sampleType: 'Product',
        sampleQuantity: 1,
        sampleUnit: 'pcs',
        sampleCost: 500,
        dispatchLeadTime: 3,
        returnPolicy: 'Returnable',
        bulkPricing: [
          { tier: 1, minQty: 1, maxQty: 99, unitPrice: 1250, discount: 'Base' },
          { tier: 2, minQty: 100, maxQty: 499, unitPrice: 1200, discount: -4.0 },
          { tier: 3, minQty: 500, maxQty: 'Above', unitPrice: 1150, discount: -8.0 }
        ]
      },
      {
        id: 2,
        description: 'Control Panel Assembly',
        quantity: 25,
        uom: 'units',
        unitPrice: 5000,
        tax: 18,
        discount: 0,
        igst: 0,
        cess: 0,
        sgst: 0,
        customs: 0,
        subtotal: 125000,
        discountAmount: 0,
        fobValue: 125000,
        igstAmount: 0,
        cessAmount: 0,
        sgstAmount: 0,
        customsAmount: 0,
        taxAmt: 23400,
        total: 125000,
        hsnCode: '8537',
        countryOfOrigin: 'India',
        warranty: '24 months',
        certifications: ['ISO 9001', 'BIS', 'CE'],
        sampleOffered: 'Yes',
        sampleType: 'Prototype',
        sampleCost: 2000,
        dispatchLeadTime: 7,
        returnPolicy: 'On Approval Basis',
        bulkPricing: [
          { tier: 1, minQty: 1, maxQty: 24, unitPrice: 5200, discount: 'Base' },
          { tier: 2, minQty: 25, maxQty: 99, unitPrice: 5000, discount: -3.8 },
          { tier: 3, minQty: 100, maxQty: 'Above', unitPrice: 4800, discount: -7.7 }
        ]
      }
    ],
    paymentTerms: [
      { id: 1, label: '', value: '30 days from invoice date', placeholder: 'e.g., 30 days from invoice date' },
      { id: 2, label: 'Advance payment:', value: '30% with PO', placeholder: 'e.g., 30% with PO' },
      { id: 3, label: 'Balance', value: '70% before dispatch', placeholder: 'e.g., 70% before dispatch' }
    ],
    paymentMethod: '',
    currency: '',
    tradeTerms: '',
    paymentDueDays: '',
    advancePayment: '',
    balancePayment: '',
    shippingAddress: '',
    shippingCountry: '',
    shippingState: '',
    shippingCity: '',
    shippingPincode: '',
    shippingMethod: '',
    sellerShippingMode: 'SELF_SHIPPING',
    destinationPort: '',
    leadTimeDays: '30',
    deliveryTerms: {
      deliveryTime: '',
      incoterms: '',
      warranty: ''
    },
    freightCharges: '', // Percentage of FOB
    insuranceCharges: '', // Percentage of FOB
    handlingClearance: '', // Fixed amount
    declarationAccepted: true
  });

  const [activeItemTab, setActiveItemTab] = useState(1);
  const [expandedBulkPricing, setExpandedBulkPricing] = useState({});
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [revisionHistoryExpanded, setRevisionHistoryExpanded] = useState(false);
  const [clarificationThreadExpanded, setClarificationThreadExpanded] = useState(false);
  const [buyerInfoExpanded, setBuyerInfoExpanded] = useState(false);
  const [itemsRequestedExpanded, setItemsRequestedExpanded] = useState(false);
  const [specialRequirementsExpanded, setSpecialRequirementsExpanded] = useState(false);
  const [rfqDetailsExpanded, setRfqDetailsExpanded] = useState(false);

  const certificationsList = ['ISO 9001', 'ISO 14001', 'BIS', 'FDA', 'ROHS', 'REACH', 'CE'];

  const addLineItem = () => {
    const newItemId = Math.max(...quotation.items.map(item => item.id), 0) + 1;
    // Get default UOM ID (try to find 'pcs' or use first option)
    const defaultUOMId = attributeUnitOptions && attributeUnitOptions.length > 0
      ? (attributeUnitOptions.find(opt => opt.label?.toLowerCase() === 'pcs')?.value || attributeUnitOptions[0]?.value || '')
      : '';
    const defaultSampleUnitId = attributeUnitOptions && attributeUnitOptions.length > 0
      ? (attributeUnitOptions.find(opt => opt.label?.toLowerCase() === 'pcs')?.value || attributeUnitOptions[0]?.value || '')
      : '';
    const newItem = {
      id: newItemId,
      description: '',
      quantity: 1,
      uom: defaultUOMId,
      unitPrice: 0,
      tax: 18,
      discount: 0,
      igst: 0,
      cess: 0,
      sgst: 0,
      subtotal: 0,
      discountAmount: 0,
      fobValue: 0,
      igstAmount: 0,
      cessAmount: 0,
      sgstAmount: 0,
      customsAmount: 0,
      freightAmount: 0,
      insuranceAmount: 0,
      handlingAmount: 0,
      cifValue: 0,
      bcdAmount: 0,
      swsAmount: 0,
      assessableValue: 0,
      taxAmt: 0,
      total: 0,
      hsnCode: '',
      countryOfOrigin: 'India',
      warranty: '',
      certifications: [],
      sampleOffered: 'No',
      sampleType: '',
      sampleQuantity: 0,
      sampleUnit: defaultSampleUnitId,
      sampleCost: 0,
      dispatchLeadTime: 0,
      returnPolicy: 'Non-Returnable',
      bulkPricing: [
        { tier: 1, minQty: 1, maxQty: '', unitPrice: 0, discount: 'Base' }
      ]
    };
    
    setQuotation(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    // Set the newly added item as active tab
    setActiveItemTab(newItemId);
  };

  const removeLineItem = (itemId) => {
    if (quotation.items.length <= 1) {
      alert('At least one line item is required.');
      return;
    }
    
    setQuotation(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    
    // If the removed item was active, switch to the first remaining item
    if (activeItemTab === itemId) {
      const remainingItems = quotation.items.filter(item => item.id !== itemId);
      if (remainingItems.length > 0) {
        setActiveItemTab(remainingItems[0].id);
      }
    }
  };

  const handleQuotationChange = (itemId, field, value) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          
          // Recalculate totals if price or tax changes
          if (field === 'unitPrice' || field === 'tax' || field === 'quantity' || field === 'discount' || field === 'igst' || field === 'cess' || field === 'sgst' || field === 'customs') {
            const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : item.unitPrice;
            const tax = field === 'tax' ? parseFloat(value) || 0 : item.tax;
            const quantity = field === 'quantity' ? parseFloat(value) || 0 : item.quantity;
            const discount = field === 'discount' ? parseFloat(value) || 0 : (item.discount || 0);
            const igst = field === 'igst' ? parseFloat(value) || 0 : (item.igst || 0);
            const cess = field === 'cess' ? parseFloat(value) || 0 : (item.cess || 0);
            const sgst = field === 'sgst' ? parseFloat(value) || 0 : (item.sgst || 0);
            const customs = field === 'customs' ? parseFloat(value) || 0 : (item.customs || 0);
            
            // Calculate subtotal
            const subtotal = unitPrice * quantity;
            // Calculate discount amount
            const discountAmount = subtotal * (discount / 100);
            // Calculate FOB value (after discount)
            const fobValue = subtotal - discountAmount;
            
            // For intra-state trade, calculate CGST, SGST, and CESS on FOB value
            if (prev.tradeType === 'IN India to India - Intra-State (Same State)') {
              const cgstAmount = fobValue * (igst / 100); // CGST uses igst field
              const sgstAmount = fobValue * (sgst / 100);
              const cessAmount = fobValue * (cess / 100);
              updated.subtotal = subtotal;
              updated.discountAmount = discountAmount;
              updated.fobValue = fobValue;
              updated.igstAmount = cgstAmount; // Store CGST in igstAmount for compatibility
              updated.cessAmount = cessAmount;
              updated.sgstAmount = sgstAmount; // Store SGST separately
              updated.total = fobValue + cgstAmount + sgstAmount + cessAmount;
            }
            // For import trade, calculate CIF, BCD, SWS, Assessable Value, and IGST
            else if (prev.tradeType === '🌎 →IN Import into India (Foreign → India)') {
              // Calculate CIF (Cost + Insurance + Freight)
              const freightChargesPercent = parseFloat(prev.freightCharges) || 0;
              const insuranceChargesPercent = parseFloat(prev.insuranceCharges) || 0;
              const freightAmount = fobValue * (freightChargesPercent / 100);
              const insuranceAmount = fobValue * (insuranceChargesPercent / 100);
              const handlingAmount = parseFloat(prev.handlingClearance) || 0;
              const cifValue = fobValue + freightAmount + insuranceAmount + handlingAmount;
              
              // Calculate BCD (Basic Customs Duty) on CIF
              const bcdAmount = cifValue * (customs / 100);
              
              // Calculate SWS (Social Welfare Surcharge) - 10% of BCD
              const swsAmount = bcdAmount * 0.10;
              
              // Calculate Assessable Value (CIF + BCD + SWS)
              const assessableValue = cifValue + bcdAmount + swsAmount;
              
              // Calculate IGST on Assessable Value
              const igstAmount = assessableValue * (igst / 100);
              
              updated.subtotal = subtotal;
              updated.discountAmount = discountAmount;
              updated.fobValue = fobValue;
              updated.freightAmount = freightAmount;
              updated.insuranceAmount = insuranceAmount;
              updated.handlingAmount = handlingAmount;
              updated.cifValue = cifValue;
              updated.bcdAmount = bcdAmount;
              updated.swsAmount = swsAmount;
              updated.assessableValue = assessableValue;
              updated.customsAmount = bcdAmount; // Store BCD in customsAmount for compatibility
              updated.igstAmount = igstAmount;
              updated.cessAmount = 0; // CESS not used in import calculation
              updated.total = assessableValue + igstAmount; // Grand Total = Assessable Value + IGST
            }
            // For inter-state trade, calculate IGST and CESS on FOB value
            else if (prev.tradeType === 'IN India to India - Inter-State (Different States)') {
              const igstAmount = fobValue * (igst / 100);
              const cessAmount = fobValue * (cess / 100);
              updated.subtotal = subtotal;
              updated.discountAmount = discountAmount;
              updated.fobValue = fobValue;
              updated.igstAmount = igstAmount;
              updated.cessAmount = cessAmount;
              updated.total = fobValue + igstAmount + cessAmount;
            }
            // For export trade, check if LUT - if LUT, no taxes; otherwise calculate IGST and CESS
            else if (prev.tradeType === 'IN → 🌎 Export from India (India → Foreign)') {
              if (prev.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST') {
                // LUT export: No IGST or CESS
                updated.subtotal = subtotal;
                updated.discountAmount = discountAmount;
                updated.fobValue = fobValue;
                updated.igstAmount = 0;
                updated.cessAmount = 0;
                updated.total = fobValue; // Grand Total = FOB Value (no taxes)
              } else {
                // Non-LUT export: Calculate IGST and CESS
                const igstAmount = fobValue * (igst / 100);
                const cessAmount = fobValue * (cess / 100);
                updated.subtotal = subtotal;
                updated.discountAmount = discountAmount;
                updated.fobValue = fobValue;
                updated.igstAmount = igstAmount;
                updated.cessAmount = cessAmount;
                updated.total = fobValue + igstAmount + cessAmount;
              }
            }
            // For Third Country Trade, calculate tax on FOB value
            else if (prev.tradeType === '🌎 Third Country Trade (Foreign → Foreign)') {
              const taxAmount = fobValue * (tax / 100);
              updated.subtotal = subtotal;
              updated.discountAmount = discountAmount;
              updated.fobValue = fobValue;
              updated.taxAmt = taxAmount;
              updated.total = fobValue + taxAmount;
            } else {
              // Fallback calculation (should not be reached with current trade types)
              updated.subtotal = subtotal;
              updated.taxAmt = (updated.subtotal * tax) / 100;
              updated.total = updated.subtotal + updated.taxAmt;
            }
          }
          
          return updated;
        }
        return item;
      })
    }));
  };

  const handleCertificationToggle = (itemId, cert) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const certs = item.certifications || [];
          const updated = certs.includes(cert)
            ? certs.filter(c => c !== cert)
            : [...certs, cert];
          return { ...item, certifications: updated };
        }
        return item;
      })
    }));
  };

  const toggleBulkPricing = (itemId) => {
    setExpandedBulkPricing(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const addBulkPricingTier = (itemId) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          // Limit to maximum 3 tiers
          if (item.bulkPricing && item.bulkPricing.length >= 3) {
            return item;
          }
          const newTier = {
            tier: (item.bulkPricing.length + 1),
            minQty: '',
            maxQty: '',
            unitPrice: '',
            discount: ''
          };
          return { ...item, bulkPricing: [...item.bulkPricing, newTier] };
        }
        return item;
      })
    }));
  };

  const removeBulkPricingTier = (itemId, tierIndex) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            bulkPricing: item.bulkPricing.filter((_, idx) => idx !== tierIndex)
          };
        }
        return item;
      })
    }));
  };

  const updateBulkPricing = (itemId, tierIndex, field, value) => {
    setQuotation(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = item.bulkPricing.map((tier, idx) => {
            if (idx === tierIndex) {
              return { ...tier, [field]: value };
            }
            return tier;
          });
          return { ...item, bulkPricing: updated };
        }
        return item;
      })
    }));
  };

  // Fetch conversations for a quotation
  const fetchConversations = useCallback(async (quotationId) => {
    if (!quotationId) return;
    
    try {
      setLoadingConversations(true);
      const response = await CommonService.get(`/quotation/${quotationId}/conversation`);
      
      if (response && response.success && response.data) {
        // Transform API response to match frontend message format
        const transformedMessages = response.data.map((conv) => ({
          id: conv.conversation_id,
          sender: conv.sender_role, // 'buyer' or 'seller'
          text: conv.message,
          timestamp: conv.created_at 
            ? new Date(conv.created_at).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/,/g, '')
            : '',
          attachments: conv.attachments || [],
          senderName: conv.sender_name || '',
          senderCompany: conv.sender_company_name || ''
        }));
        
        setMessages(transformedMessages);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Don't show error toast for 404 (quotation might not have conversations yet)
      // Don't show error toast for 401 (authentication) if component is unmounting
      if (error?.response?.status !== 404 && error?.response?.status !== 401) {
        // Only show error if it's not an auth error (which might happen during navigation)
        const errorMessage = error?.response?.data?.message || error?.message || '';
        if (!errorMessage.toLowerCase().includes('token') && !errorMessage.toLowerCase().includes('authentication')) {
          toast.error('Failed to load conversation messages');
        }
      }
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // Send a new message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    // Check if quotation has been created
    if (!quotationDbId) {
      toast.warning('Please save the quotation first (as draft or submit) before sending messages');
      return;
    }
    
    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX
    
    try {
      setSendingMessage(true);
      
      const response = await CommonService.post(
        `/quotation/${quotationDbId}/conversation`,
        {
          message: messageText,
          attachments: [] // Can be extended later to support file attachments
        }
      );
      
      if (response && response.success) {
        // Refresh conversations to get the new message with all details
        await fetchConversations(quotationDbId);
        toast.success('Message sent successfully');
      } else {
        throw new Error(response?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message text on error
      setNewMessage(messageText);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) {
      console.log('No files selected');
      return;
    }

    // Validate file types and sizes
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
      const isValidSize = file.size <= maxSize;

      if (isValidType && isValidSize) {
        validFiles.push(file);
        console.log(`✅ Valid file: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
      } else {
        invalidFiles.push({
          name: file.name,
          reason: !isValidType ? 'Invalid file type' : 'File size exceeds 10MB'
        });
      }
    });

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(invalid => {
        toast.error(`${invalid.name}: ${invalid.reason}`);
      });
    }

    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} file(s) added successfully`);
    }

    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  // Helper functions for warranty parsing
  const parseWarranty = (warrantyString) => {
    if (!warrantyString || warrantyString.trim() === '') return { value: '', unit: '' };
    const match = warrantyString.match(/^(\d+)\s*(months?|years?|days?|weeks?)$/i);
    if (match) {
      const value = match[1];
      const unit = match[2].toLowerCase();
      // Normalize unit (remove 's' if present for matching)
      const normalizedUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
      const unitKey = Object.keys(WARRANTY_PERIOD_UNITS).find(
        key => {
          const mapValue = WARRANTY_PERIOD_UNITS[key].toLowerCase();
          return mapValue === unit || mapValue === normalizedUnit || mapValue === unit + 's';
        }
      ) || 'MONTHS';
      return { value, unit: unitKey };
    }
    // Try to extract just the number if format doesn't match
    const numberMatch = warrantyString.match(/\d+/);
    if (numberMatch) {
      return { value: numberMatch[0], unit: 'MONTHS' };
    }
    return { value: '', unit: 'MONTHS' };
  };

  const formatWarranty = (value, unit) => {
    const numValue = value === '' || value === null || value === undefined ? '' : String(value).trim();
    if (!numValue || numValue === '0') return '';
    return `${numValue} ${WARRANTY_PERIOD_UNITS[unit]}`;
  };

  // Helper functions for delivery time parsing
  const parseDeliveryTime = (deliveryTimeString) => {
    if (!deliveryTimeString || deliveryTimeString.trim() === '') return { value: '', unit: '' };
    // Handle ranges like "4-6 weeks"
    const rangeMatch = deliveryTimeString.match(/^(\d+)\s*-\s*(\d+)\s*(weeks?|days?|months?)$/i);
    if (rangeMatch) {
      // For ranges, use the first number
      const value = rangeMatch[1];
      const unit = rangeMatch[3].toLowerCase();
      const normalizedUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
      const unitKey = Object.keys(DELIVERY_TIME_UNITS).find(
        key => {
          const mapValue = DELIVERY_TIME_UNITS[key].toLowerCase();
          return mapValue === unit || mapValue === normalizedUnit || mapValue === unit + 's';
        }
      ) || 'WEEKS';
      return { value, unit: unitKey };
    }
    // Handle single values like "4 weeks"
    const match = deliveryTimeString.match(/^(\d+)\s*(weeks?|days?|months?)$/i);
    if (match) {
      const value = match[1];
      const unit = match[2].toLowerCase();
      const normalizedUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
      const unitKey = Object.keys(DELIVERY_TIME_UNITS).find(
        key => {
          const mapValue = DELIVERY_TIME_UNITS[key].toLowerCase();
          return mapValue === unit || mapValue === normalizedUnit || mapValue === unit + 's';
        }
      ) || 'WEEKS';
      return { value, unit: unitKey };
    }
    // Try to extract just the number if format doesn't match
    const numberMatch = deliveryTimeString.match(/\d+/);
    if (numberMatch) {
      return { value: numberMatch[0], unit: 'WEEKS' };
    }
    return { value: '', unit: 'WEEKS' };
  };

  const formatDeliveryTime = (value, unit) => {
    const numValue = value === '' || value === null || value === undefined ? '' : String(value).trim();
    if (!numValue || numValue === '0') return '';
    return `${numValue} ${DELIVERY_TIME_UNITS[unit]}`;
  };


  const handleShippingChange = (field, value) => {
    setQuotation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeliveryTermsChange = (field, value) => {
    setQuotation(prev => ({
      ...prev,
      deliveryTerms: {
        ...prev.deliveryTerms,
        [field]: value
      }
    }));
  };


  const getDomesticTotals = () => {
    const totals = quotation.items.reduce((acc, item) => ({
      subtotal: acc.subtotal + (item.subtotal || 0),
      discountAmount: acc.discountAmount + (item.discountAmount || 0),
      fobValue: acc.fobValue + (item.fobValue || 0),
      igstAmount: acc.igstAmount + (item.igstAmount || 0),
      cessAmount: acc.cessAmount + (item.cessAmount || 0),
      sgstAmount: acc.sgstAmount + (item.sgstAmount || 0),
      customsAmount: acc.customsAmount + (item.customsAmount || 0),
      freightAmount: acc.freightAmount + (item.freightAmount || 0),
      insuranceAmount: acc.insuranceAmount + (item.insuranceAmount || 0),
      handlingAmount: acc.handlingAmount + (item.handlingAmount || 0),
      cifValue: acc.cifValue + (item.cifValue || 0),
      bcdAmount: acc.bcdAmount + (item.bcdAmount || 0),
      swsAmount: acc.swsAmount + (item.swsAmount || 0),
      assessableValue: acc.assessableValue + (item.assessableValue || 0),
      total: acc.total + (item.total || 0)
    }), { 
      subtotal: 0, 
      discountAmount: 0, 
      fobValue: 0, 
      igstAmount: 0, 
      cessAmount: 0, 
      sgstAmount: 0, 
      customsAmount: 0,
      freightAmount: 0,
      insuranceAmount: 0,
      handlingAmount: 0,
      cifValue: 0,
      bcdAmount: 0,
      swsAmount: 0,
      assessableValue: 0,
      total: 0 
    });
    return totals;
  };

  // Fetch seller's company profile to get location and load states for India if needed
  useEffect(() => {
    const fetchSellerProfile = async () => {
      try {
        const response = await CommonService.get('/company/my-profile');
        if (response && response.success && response.data) {
          const profile = response.data;
          const sellerCountry = profile.address?.country || profile.country || '';
          const sellerState = profile.address?.state || profile.state || '';
          
          setSellerLocation({
            country: sellerCountry,
            state: sellerState,
            countryName: sellerCountry,
            stateName: sellerState
          });
          
          // If seller is in India, load states for India to enable state ID lookup
          // Only load if states are not already loaded (to avoid unnecessary API calls and infinite loops)
          if (rawCountries.length > 0) {
            const sellerCountryName = String(sellerCountry).toLowerCase();
            if (sellerCountryName === 'india' || sellerCountryName.includes('india')) {
              // Find India's country ID
              const indiaCountry = rawCountries.find(c => {
                const countryName = (c.label || c.country_name || c.country || '').toLowerCase();
                return countryName === 'india' || countryName.includes('india');
              });
              
              // Only load states if not already loaded (check if we have more than 1 state, as India has many states)
              if (indiaCountry && indiaCountry.id && (!rawStates || rawStates.length <= 1)) {
                updateListsForCountry(indiaCountry.id);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching seller profile:', error);
        // Continue without seller location - trade type will be set when RFQ data is loaded
      }
    };

    if (rawCountries.length > 0) {
      fetchSellerProfile();
    }
  }, [rawCountries.length]); // Only depend on rawCountries length to avoid infinite loop

  // Fetch RFQ data from backend
  useEffect(() => {
    const fetchRFQData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const rfqIdFromQuery = searchParams.get('rfqId');
      const editModeFromQuery = searchParams.get('edit') === 'true';
      // Check state, query string, or rfq_id from state (in case different formats are used)
      const rfqIdFromState =
        location?.state?.rfqId ||
        location?.state?.rfq_id ||
        (rfqIdFromQuery ? `REQ-${rfqIdFromQuery}` : null);
      if (!rfqIdFromState) {
        toast.error('RFQ ID not provided');
        return;
      }

      // Extract numeric ID from formatted string (e.g., "REQ-123" or "RFQ-123" -> 123)
      // or use the value directly if it's already a number
      let numericRfqId = rfqIdFromState;
      if (typeof rfqIdFromState === 'string') {
        // Try to extract number from formats like "REQ-123", "RFQ-123", "REQ-2025-123", etc.
        // Match any sequence of digits at the end of the string
        const match = rfqIdFromState.match(/(\d+)$/);
        if (match && match[1]) {
          numericRfqId = parseInt(match[1], 10);
        } else {
          // If no match, try parsing as number directly
          const parsed = parseInt(rfqIdFromState, 10);
          if (!isNaN(parsed)) {
            numericRfqId = parsed;
          } else {
            toast.error(`Invalid RFQ ID format: ${rfqIdFromState}`);
            return;
          }
        }
      } else if (typeof rfqIdFromState === 'number') {
        numericRfqId = rfqIdFromState;
      } else {
        toast.error(`Invalid RFQ ID type: ${typeof rfqIdFromState}`);
        return;
      }

      if (!numericRfqId || isNaN(numericRfqId) || numericRfqId <= 0) {
        toast.error(`Invalid RFQ ID: ${rfqIdFromState} (extracted: ${numericRfqId})`);
        return;
      }

      // Store the numeric RFQ ID for submission
      setRfqId(numericRfqId);

      try {
        setLoadingRFQ(true);
        const response = await CommonService.get(`/rfq/${numericRfqId}`);
        
        if (response && response.success && response.data) {
          const rfq = response.data;
          
          // Format date from backend (assuming it's in ISO format or similar)
          const formatDate = (dateString) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return '';
              return date.toISOString().split('T')[0];
            } catch (e) {
              return '';
            }
          };

          // Format currency for price range
          // currency might be an ID or currency code - map it to currency code first
          const formatPriceRange = (min, max, currency) => {
            if (!min && !max) return '';
            
            // Map currency ID to currency code if needed
            let currencyCode = currency || 'INR';
            if (currency && currencyOptions && currencyOptions.length > 0) {
              const currencyOption = findCurrencyOption(currency);
              if (currencyOption) {
                // Use currency code from the option
                currencyCode = currencyOption.currencyCode || currencyOption.value || currency;
              } else {
                // If not found, use the currency value as-is (might already be a code)
                currencyCode = currency;
              }
            }
            
            // Get currency symbol
            const symbol = currencyCode === 'INR' ? '₹' : currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '€' : currencyCode === 'GBP' ? '£' : currencyCode;
            
            if (min && max) {
              return `${symbol}${min} - ${symbol}${max} per unit`;
            } else if (min) {
              return `${symbol}${min} per unit (min)`;
            } else if (max) {
              return `${symbol}${max} per unit (max)`;
            }
            return '';
          };

          // Map procurement method value to display label
          const formatProcurementMethod = (preferences) => {
            if (!preferences) return '';
            const procurementMethodMap = {
              'buy-with-help': "Buy with help from the platform's procurement team",
              'buy-directly': "Buy directly by posting my needs on the platform"
            };
            return procurementMethodMap[preferences] || preferences;
          };

          // Buyer information is now included in the RFQ response via JOIN with user_login and company_profile tables
          let buyerInfo = {
            companyName: rfq.company_name || '',
            contactPerson: rfq.buyer_name || rfq.name || '',
            rating: 0, // Rating not available from RFQ response
            email: rfq.buyer_email || rfq.email || '',
            contactNumber: rfq.buyer_phone || rfq.phone || '',
            deliveryAddress: rfq.shipping_address || '',
            deliveryCity: rfq.shipping_city || '',
            deliveryState: rfq.shipping_state || '',
            deliveryCountry: rfq.shipping_country || '',
            deliveryPincode: rfq.shipping_pincode || ''
          };

          // Map backend response to frontend structure
          const mappedRfqData = {
            rfqId: `RFQ-${rfq.rfq_id || numericRfqId}`,
            rfqDate: formatDate(rfq.created_at),
            rfqStatus: rfq.status ? rfq.status.charAt(0).toUpperCase() + rfq.status.slice(1) : 'Open',
            requiredByDate: formatDate(rfq.requiredByDate) || formatDate(rfq.created_at),
            paymentTerms: rfq.payment_terms || '',
            tradeTerms: rfq.trade_terms || '',
            shippingAddress: rfq.shipping_address || '',
            shippingCountry: rfq.shipping_country || '',
            shippingState: rfq.shipping_state || '',
            shippingCity: rfq.shipping_city || '',
            shippingPincode: rfq.shipping_pincode || '',
            shippingMethod: rfq.preferred_shipping_method || '',
            destinationPort: rfq.destination_port || '',
            leadTimeDays: rfq.lead_time_days ? String(rfq.lead_time_days) : '',
            buyer: buyerInfo,
            items: [
              {
                id: 1,
                description: rfq.product_name || '',
                specifications: rfq.product_description || '',
                quantity: rfq.product_quantity || 0,
                // Map UOM ID to label for display (rfqData uses label for display)
                uom: getUOMLabelFromId(mapRFQUnitToUOMId(rfq.quantity_unit)) || rfq.quantity_unit || '',
                requiredDate: formatDate(rfq.requiredByDate) || formatDate(rfq.created_at),
                productCategory: rfq.category_name || '',
                targetPriceRange: formatPriceRange(rfq.target_price_min, rfq.target_price_max, rfq.target_price_currency),
                procurementMethod: formatProcurementMethod(rfq.preferences),
                // Store original RFQ values for re-formatting when options load
                _originalPriceMin: rfq.target_price_min,
                _originalPriceMax: rfq.target_price_max,
                _originalCurrency: rfq.target_price_currency
              }
            ],
            specialRequirements: rfq.special_requirements || ''
          };

          setRfqData(mappedRfqData);

          const editMode = Boolean(location?.state?.editMode || editModeFromQuery);

          // Populate quotation line items from RFQ data (skip when editing an existing draft)
          if (!editMode) {
          // Map RFQ product information to quotation line items
          // Map RFQ unit to UOM ID for backend storage
          console.log('Loading RFQ data - quantity_unit from RFQ:', rfq.quantity_unit, 'Type:', typeof rfq.quantity_unit);
          console.log('UOM Options available at load time:', attributeUnitOptions?.length || 0, 'Loading:', uomLoading);
          const mappedUOMId = mapRFQUnitToUOMId(rfq.quantity_unit);
          // If no ID found, try to find a default (e.g., 'pcs' or first option)
          const defaultUOMId = mappedUOMId || (attributeUnitOptions && attributeUnitOptions.length > 0 
            ? String(attributeUnitOptions.find(opt => opt.label?.toLowerCase() === 'pcs')?.value || attributeUnitOptions[0]?.value || '')
            : '');
          console.log('Mapped UOM ID result:', defaultUOMId);
          const mappedLineItems = [
            {
              id: 1,
              description: rfq.product_name || '',
              quantity: rfq.product_quantity || 1,
              uom: defaultUOMId,
              unitPrice: rfq.target_price_min || 0, // Use minimum target price as initial unit price
              tax: '', // User must fill
              discount: 0,
              igst: 0,
              cess: 0,
              sgst: 0,
              customs: 0,
              subtotal: 0, // Will be calculated
              discountAmount: 0, // Will be calculated
              fobValue: 0, // Will be calculated
              igstAmount: 0, // Will be calculated
              cessAmount: 0, // Will be calculated
              sgstAmount: 0, // Will be calculated
              customsAmount: 0, // Will be calculated
              freightAmount: 0,
              insuranceAmount: 0,
              handlingAmount: 0,
              cifValue: 0, // Will be calculated
              bcdAmount: 0, // Will be calculated
              swsAmount: 0, // Will be calculated
              assessableValue: 0, // Will be calculated
              taxAmt: 0, // Will be calculated
              total: 0, // Will be calculated
              hsnCode: rfq.hsn_code || '', // HSN code from RFQ if available
              countryOfOrigin: '', // User must fill
              warranty: '', // Can be filled by seller
              certifications: [], // Can be filled by seller
              sampleOffered: '', // User must select
              sampleType: '',
              sampleQuantity: 0,
              sampleUnit: '',
              sampleCost: 0,
              dispatchLeadTime: '', // User must fill
              returnPolicy: '', // User must fill
              bulkPricing: [] // User must add if needed
            }
          ];

          // Update quotation with line items from RFQ
          setQuotation(prev => {
            // Calculate initial values for the line item
            const initialQuantity = mappedLineItems[0].quantity;
            const initialUnitPrice = mappedLineItems[0].unitPrice;
            const initialSubtotal = initialQuantity * initialUnitPrice;
            mappedLineItems[0].subtotal = initialSubtotal;
            mappedLineItems[0].fobValue = initialSubtotal;
            mappedLineItems[0].total = initialSubtotal;

            return {
              ...prev,
              items: mappedLineItems
            };
          });

          // Set the first item as active tab
          setActiveItemTab(1);

          // Trigger calculation for the first item to ensure all tax amounts are calculated
          setTimeout(() => {
            if (mappedLineItems[0] && mappedLineItems[0].id) {
              handleQuotationChange(mappedLineItems[0].id, 'unitPrice', mappedLineItems[0].unitPrice);
            }
          }, 100);
          }

          // Load states for India if buyer is in India (needed for state ID lookup)
          // Only load if states are not already loaded (to avoid unnecessary API calls)
          const buyerCountryName = getCountryName(rfq.shipping_country || '').toLowerCase();
          if ((buyerCountryName === 'india' || buyerCountryName.includes('india')) && 
              rawCountries.length > 0 && 
              (!rawStates || rawStates.length === 0)) {
            const indiaCountry = rawCountries.find(c => {
              const countryName = (c.label || c.country_name || c.country || '').toLowerCase();
              return countryName === 'india' || countryName.includes('india');
            });
            if (indiaCountry && indiaCountry.id) {
              updateListsForCountry(indiaCountry.id);
            }
          }

          // Auto-populate currency from RFQ if available
          if (rfq.target_price_currency) {
            // Map RFQ currency to quotation currency
            // RFQ currency might be stored as currency code (e.g., "INR", "USD") or ID
            const rfqCurrency = rfq.target_price_currency;
            console.log('RFQ Currency from data:', rfqCurrency);
            
            // Try to find matching currency option
            const currencyOption = findCurrencyOption(rfqCurrency);
            if (currencyOption) {
              // Use the currency value (ID or code) that matches the dropdown
              const mappedCurrency = currencyOption.value || currencyOption.currencyCode || rfqCurrency;
              console.log('Mapped currency from RFQ:', mappedCurrency, 'from option:', currencyOption);
              setQuotation(prev => ({
                ...prev,
                currency: mappedCurrency
              }));
            } else {
              // If not found in options, use the RFQ currency value as-is
              // It might be a valid currency code that will work
              console.log('Currency option not found, using RFQ value as-is:', rfqCurrency);
              setQuotation(prev => ({
                ...prev,
                currency: rfqCurrency
              }));
            }
          }

          // Auto-determine and set trade type based on buyer and seller locations
          // NOTE: Both buyer and seller locations are stored as labels (strings)
          // - Buyer: rfq.shipping_country/shipping_state are labels (strings) from database
          // - Seller: sellerLocation.country/state are labels (strings) from company profile
          // The determineTradeType() function compares labels directly (case-insensitive)
          const buyerCountry = rfq.shipping_country || ''; // Label (string)
          const buyerState = rfq.shipping_state || ''; // Label (string)
          const sellerCountry = sellerLocation.country || sellerLocation.countryName || ''; // Label (string)
          const sellerState = sellerLocation.state || sellerLocation.stateName || ''; // Label (string)

          if (!editMode && (buyerCountry || sellerCountry)) {
            // Both locations are labels - compare directly (no ID conversion needed)
            const autoTradeType = determineTradeType(buyerCountry, buyerState, sellerCountry, sellerState);
            setQuotation(prev => {
              const updates = { tradeType: autoTradeType };
              // Don't set default exportTaxType - user must select
              return { ...prev, ...updates };
            });
          }

          if (editMode) {
            try {
              const quoteResponse = await CommonService.get(`/quotation/rfq/${numericRfqId}`);
              if (quoteResponse?.success && quoteResponse.data) {
                const stored = quoteResponse.data;
                if ((stored.quote_status || '').toString().toLowerCase() === 'draft') {
                  const mapped = mapStoredQuotationToForm(stored);
                  setQuotationDbId(stored.quotation_id);
                  setExistingAttachments(mapped.existingAttachments || []);
                  const { existingAttachments: _savedAttachments, ...formData } = mapped;
                  setQuotation((prev) => ({ ...prev, ...formData }));
                  if (formData.items?.length) {
                    setActiveItemTab(formData.items[0].id);
                  }
                  toast.info('Draft quotation loaded. Update and submit when ready.');
                } else {
                  toast.warning('This quotation is no longer a draft.');
                }
              } else {
                toast.error('No draft quotation found for this RFQ on your seller account.');
              }
            } catch (draftError) {
              console.error('Error loading draft quotation:', draftError);
              const apiMessage =
                draftError?.response?.data?.message ||
                draftError?.response?.data?.error ||
                draftError?.message;
              toast.error(apiMessage || 'Failed to load draft quotation for editing');
            }
          }
        } else {
          toast.error('Failed to load RFQ data');
        }
      } catch (error) {
        console.error('Error fetching RFQ data:', error);
        console.error('Error details:', {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
          url: error?.config?.url,
          rfqIdFromState,
          numericRfqId
        });
        
        let errorMessage = 'Failed to load RFQ data';
        if (error?.response?.status === 404) {
          errorMessage = `RFQ with ID ${numericRfqId} not found`;
        } else if (error?.response?.status === 400) {
          errorMessage = error?.response?.data?.error || `Invalid RFQ ID: ${numericRfqId}`;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      } finally {
        setLoadingRFQ(false);
      }
    };

    fetchRFQData();
  }, [location?.state?.rfqId, location?.state?.editMode, location.search]);

  // Update trade type when seller location or RFQ data becomes available
  useEffect(() => {
    if (rfqData.rfqId && sellerLocation.country) {
      // NOTE: Both buyer and seller locations are stored as labels (strings)
      // - Buyer: rfqData.shippingCountry/shippingState are labels (strings) from database
      // - Seller: sellerLocation.country/state are labels (strings) from company profile
      // The determineTradeType() function compares labels directly (case-insensitive)
      const buyerCountry = rfqData.shippingCountry || ''; // Label (string)
      const buyerState = rfqData.shippingState || ''; // Label (string)
      const sellerCountry = sellerLocation.country || sellerLocation.countryName || ''; // Label (string)
      const sellerState = sellerLocation.state || sellerLocation.stateName || ''; // Label (string)

      if (buyerCountry || sellerCountry) {
        // Both locations are labels - compare directly (no ID conversion needed)
        const autoTradeType = determineTradeType(buyerCountry, buyerState, sellerCountry, sellerState);
        setQuotation(prev => {
          // Only update if trade type hasn't been set or needs to change
          if (prev.tradeType !== autoTradeType) {
            const updates = { tradeType: autoTradeType };
            // Don't set default exportTaxType - user must select
            return { ...prev, ...updates };
          }
          return prev;
        });
      }
    }
  }, [sellerLocation, rfqData.rfqId, rfqData.shippingCountry, rfqData.shippingState]);

  // Recalculate all items when import charges change
  useEffect(() => {
    if (quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && quotation.items.length > 0) {
      quotation.items.forEach(item => {
        // Trigger recalculation by updating unitPrice (which will use the latest freightCharges, insuranceCharges, handlingClearance)
        handleQuotationChange(item.id, 'unitPrice', item.unitPrice);
      });
    }
  }, [quotation.freightCharges, quotation.insuranceCharges, quotation.handlingClearance]);

  // Recalculate all items when export tax type changes
  useEffect(() => {
    if (quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.items.length > 0) {
      quotation.items.forEach(item => {
        // Trigger recalculation by updating unitPrice (which will use the latest exportTaxType)
        handleQuotationChange(item.id, 'unitPrice', item.unitPrice);
      });
    }
  }, [quotation.exportTaxType]);

  // Fetch conversations when quotationDbId is available
  useEffect(() => {
    if (quotationDbId) {
      fetchConversations(quotationDbId);
    }
  }, [quotationDbId, fetchConversations]);

  // Function to validate quotation before submission
  const validateQuotation = () => {
    const errors = [];
    console.log('=== VALIDATION START ===');
    console.log('RFQ ID:', rfqId);
    console.log('Quotation:', quotation);

    // ========== RFQ ID Validation ==========
    if (!rfqId || rfqId === null || rfqId === undefined || rfqId === '') {
      errors.push('RFQ ID is missing');
      console.log('❌ RFQ ID validation failed');
    }

    // ========== Quotation Information Section Validation ==========
    // Quote Date
    const quoteDateStr = String(quotation.quoteDate || '').trim();
    if (!quotation.quoteDate || quotation.quoteDate === null || quotation.quoteDate === undefined || quoteDateStr === '' || quoteDateStr === 'undefined' || quoteDateStr === 'null') {
      errors.push('Quotation Information: Quote Date is required');
      console.log('❌ Quote Date validation failed:', quotation.quoteDate);
    }

    // Validity Days
    const validityDaysNum = Number(quotation.validityDays);
    if (quotation.validityDays === null || quotation.validityDays === undefined || quotation.validityDays === '' || isNaN(validityDaysNum) || validityDaysNum <= 0) {
      errors.push('Quotation Information: Validity Days must be greater than 0');
      console.log('❌ Validity Days validation failed:', quotation.validityDays);
    }

    // Quote Status
    const quoteStatusStr = String(quotation.quoteStatus || '').trim();
    if (!quotation.quoteStatus || quotation.quoteStatus === null || quotation.quoteStatus === undefined || quoteStatusStr === '' || quoteStatusStr === 'undefined' || quoteStatusStr === 'null') {
      errors.push('Quotation Information: Quote Status is required');
      console.log('❌ Quote Status validation failed:', quotation.quoteStatus);
    }

    // ========== Trade Type & Tax Configuration Section Validation ==========
    // Trade Type
    const tradeTypeStr = String(quotation.tradeType || '').trim();
    if (!quotation.tradeType || quotation.tradeType === null || quotation.tradeType === undefined || tradeTypeStr === '' || tradeTypeStr === 'undefined' || tradeTypeStr === 'null') {
      errors.push('Trade Type & Tax Configuration: Trade Type is required');
      console.log('❌ Trade Type validation failed:', quotation.tradeType);
    }

    // Currency
    const currencyStr = String(quotation.currency || '').trim();
    if (!quotation.currency || quotation.currency === null || quotation.currency === undefined || currencyStr === '' || currencyStr === 'undefined' || currencyStr === 'null') {
      errors.push('Trade Type & Tax Configuration: Currency is required');
      console.log('❌ Currency validation failed:', quotation.currency);
    }

    // Export Tax Type (required for Export from India)
    if (quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)') {
      if (!quotation.exportTaxType || quotation.exportTaxType.trim() === '') {
        errors.push('Trade Type & Tax Configuration: Export Tax Type is required');
      } else {
        // ARN Number (required if LUT)
        if (quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST') {
          if (!quotation.arnNumber || quotation.arnNumber.trim() === '') {
            errors.push('Trade Type & Tax Configuration: ARN Number is required for LUT Export');
          }
        }
        // IGST Registration Number (required if Non-LUT)
        else if (quotation.exportTaxType === 'Non-LUT - IGST Applicable') {
          if (!quotation.igstRegistrationNumber || quotation.igstRegistrationNumber.trim() === '') {
            errors.push('Trade Type & Tax Configuration: IGST Registration Number is required for Non-LUT Export');
          }
        }
      }
    }

    // ========== Line Items Section Validation ==========
    if (!quotation.items || quotation.items === null || quotation.items === undefined || !Array.isArray(quotation.items) || quotation.items.length === 0) {
      errors.push('Line Items: At least one line item is required');
    } else {
      quotation.items.forEach((item, index) => {
        const itemNum = index + 1;
        
        // Description
        if (!item || !item.description || item.description === null || item.description === undefined || String(item.description).trim() === '') {
          errors.push(`Line Item ${itemNum}: Description is required`);
        }

        // HSN/SAC Code
        if (!item || !item.hsnCode || item.hsnCode === null || item.hsnCode === undefined || String(item.hsnCode).trim() === '') {
          errors.push(`Line Item ${itemNum}: HSN/SAC Code is required`);
        }

        // Quantity
        const quantityNum = Number(item.quantity);
        if (!item || item.quantity === null || item.quantity === undefined || isNaN(quantityNum) || quantityNum <= 0) {
          errors.push(`Line Item ${itemNum}: Quantity must be greater than 0`);
        }

        // Unit of Measure (UOM)
        if (!item || !item.uom || item.uom === null || item.uom === undefined || String(item.uom).trim() === '') {
          errors.push(`Line Item ${itemNum}: Unit of Measure (UOM) is required`);
        }

        // Unit Price
        const unitPriceNum = Number(item.unitPrice);
        if (!item || item.unitPrice === null || item.unitPrice === undefined || isNaN(unitPriceNum) || unitPriceNum <= 0) {
          errors.push(`Line Item ${itemNum}: Unit Price must be greater than 0`);
        }

        // Tax fields validation based on trade type
        if (quotation.tradeType === 'IN India to India - Intra-State (Same State)') {
          // CGST (stored in igst field for intra-state)
          if (item.igst === undefined || item.igst === null || item.igst === '') {
            errors.push(`Line Item ${itemNum}: CGST % is required for Intra-State trade`);
          } else if (parseFloat(item.igst) < 0) {
            errors.push(`Line Item ${itemNum}: CGST % must be 0 or greater`);
          }
          
          // SGST
          if (item.sgst === undefined || item.sgst === null || item.sgst === '') {
            errors.push(`Line Item ${itemNum}: SGST % is required for Intra-State trade`);
          } else if (parseFloat(item.sgst) < 0) {
            errors.push(`Line Item ${itemNum}: SGST % must be 0 or greater`);
          }
          
          // CESS
          if (item.cess === undefined || item.cess === null || item.cess === '') {
            errors.push(`Line Item ${itemNum}: CESS % is required for Intra-State trade`);
          } else if (parseFloat(item.cess) < 0) {
            errors.push(`Line Item ${itemNum}: CESS % must be 0 or greater`);
          }
        } else if (quotation.tradeType === 'IN India to India - Inter-State (Different States)') {
          // IGST
          if (item.igst === undefined || item.igst === null || item.igst === '') {
            errors.push(`Line Item ${itemNum}: IGST % is required for Inter-State trade`);
          } else if (parseFloat(item.igst) < 0) {
            errors.push(`Line Item ${itemNum}: IGST % must be 0 or greater`);
          }
          
          // CESS
          if (item.cess === undefined || item.cess === null || item.cess === '') {
            errors.push(`Line Item ${itemNum}: CESS % is required for Inter-State trade`);
          } else if (parseFloat(item.cess) < 0) {
            errors.push(`Line Item ${itemNum}: CESS % must be 0 or greater`);
          }
        } else if (quotation.tradeType === '🌎 →IN Import into India (Foreign → India)') {
          // Customs (BCD)
          if (item.customs === undefined || item.customs === null || item.customs === '') {
            errors.push(`Line Item ${itemNum}: Customs % (BCD) is required for Import trade`);
          } else if (parseFloat(item.customs) < 0) {
            errors.push(`Line Item ${itemNum}: Customs % must be 0 or greater`);
          }
          
          // IGST
          if (item.igst === undefined || item.igst === null || item.igst === '') {
            errors.push(`Line Item ${itemNum}: IGST % is required for Import trade`);
          } else if (parseFloat(item.igst) < 0) {
            errors.push(`Line Item ${itemNum}: IGST % must be 0 or greater`);
          }
          
          // CESS
          if (item.cess === undefined || item.cess === null || item.cess === '') {
            errors.push(`Line Item ${itemNum}: CESS % is required for Import trade`);
          } else if (parseFloat(item.cess) < 0) {
            errors.push(`Line Item ${itemNum}: CESS % must be 0 or greater`);
          }
        } else if (quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)') {
          // For LUT Export, IGST and CESS should be 0 (no validation needed as they're auto-set)
          // For Non-LUT Export, IGST and CESS are required
          if (quotation.exportTaxType === 'Non-LUT - IGST Applicable') {
            if (item.igst === undefined || item.igst === null || item.igst === '') {
              errors.push(`Line Item ${itemNum}: IGST % is required for Non-LUT Export`);
            } else if (parseFloat(item.igst) < 0) {
              errors.push(`Line Item ${itemNum}: IGST % must be 0 or greater`);
            }
            
            if (item.cess === undefined || item.cess === null || item.cess === '') {
              errors.push(`Line Item ${itemNum}: CESS % is required for Non-LUT Export`);
            } else if (parseFloat(item.cess) < 0) {
              errors.push(`Line Item ${itemNum}: CESS % must be 0 or greater`);
            }
          }
        } else if (quotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)') {
          // Tax
          if (item.tax === undefined || item.tax === null || item.tax === '') {
            errors.push(`Line Item ${itemNum}: Tax % is required for Third Country Trade`);
          } else if (parseFloat(item.tax) < 0) {
            errors.push(`Line Item ${itemNum}: Tax % must be 0 or greater`);
          }
        }
      });
    }

    console.log('=== VALIDATION END ===');
    console.log('Total errors:', errors.length);
    if (errors.length > 0) {
      console.error('❌ Validation FAILED - Errors:', errors);
      // Log each error separately for better visibility
      errors.forEach((error, idx) => {
        console.error(`  ${idx + 1}. ${error}`);
      });
    } else {
      console.log('✅ Validation PASSED');
    }

    return errors;
  };

  // Helper function to check if form is valid (for button disable state)
  const isFormValid = () => {
    const errors = validateQuotation();
    const hasDeclaration = quotation.declarationAccepted === true;
    return errors.length === 0 && hasDeclaration;
  };

  // Helper function to check if a value is actually filled by user
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && isNaN(value)) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // Function to prepare quotation payload for backend
  const prepareQuotationPayload = (status = 'Draft') => {
    // Map line items to backend format
    const lineItems = quotation.items.map((item, index) => {
      // Normalize UOM to string - handle object, number, or string values
      let uomValue = item.uom;
      if (uomValue !== null && uomValue !== undefined) {
        if (typeof uomValue === 'object' && uomValue.value !== undefined) {
          // Handle Select component object {value: ..., label: ...}
          uomValue = uomValue.value || uomValue.label || '';
        }
        // Convert to string if it's a number
        if (typeof uomValue === 'number') {
          uomValue = String(uomValue);
        }
        // Ensure it's a string and trim it
        uomValue = typeof uomValue === 'string' ? uomValue.trim() : String(uomValue).trim();
      } else {
        uomValue = '';
      }

      const lineItem = {
        item_sequence: index + 1,
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        uom: uomValue,
        unit_price: parseFloat(item.unitPrice) || 0,
        discount: parseFloat(item.discount) || 0,
        discount_amount: parseFloat(item.discountAmount) || 0,
        subtotal: parseFloat(item.subtotal) || 0,
        fob_value: parseFloat(item.fobValue) || 0,
        total: parseFloat(item.total) || 0
      };

      // Only include optional fields if they have values
      if (hasValue(item.hsnCode)) lineItem.hsn_code = item.hsnCode;
      
      // Tax fields - only include if they have values (not 0 or empty)
      if (hasValue(item.tax) && parseFloat(item.tax) > 0) lineItem.tax = parseFloat(item.tax);
      if (hasValue(item.igst) && parseFloat(item.igst) > 0) {
        lineItem.igst = parseFloat(item.igst);
        lineItem.igst_amount = parseFloat(item.igstAmount) || 0;
        lineItem.cgst = parseFloat(item.igst); // CGST uses igst field for intra-state
      }
      if (hasValue(item.sgst) && parseFloat(item.sgst) > 0) {
        lineItem.sgst = parseFloat(item.sgst);
        lineItem.sgst_amount = parseFloat(item.sgstAmount) || 0;
      }
      if (hasValue(item.cess) && parseFloat(item.cess) > 0) {
        lineItem.cess = parseFloat(item.cess);
        lineItem.cess_amount = parseFloat(item.cessAmount) || 0;
      }
      if (hasValue(item.customs) && parseFloat(item.customs) > 0) {
        lineItem.customs = parseFloat(item.customs);
        lineItem.customs_amount = parseFloat(item.customsAmount) || 0;
      }
      if (hasValue(item.freightAmount) && parseFloat(item.freightAmount) > 0) lineItem.freight_amount = parseFloat(item.freightAmount);
      if (hasValue(item.insuranceAmount) && parseFloat(item.insuranceAmount) > 0) lineItem.insurance_amount = parseFloat(item.insuranceAmount);
      if (hasValue(item.handlingAmount) && parseFloat(item.handlingAmount) > 0) lineItem.handling_amount = parseFloat(item.handlingAmount);
      if (hasValue(item.cifValue) && parseFloat(item.cifValue) > 0) lineItem.cif_value = parseFloat(item.cifValue);
      if (hasValue(item.bcdAmount) && parseFloat(item.bcdAmount) > 0) lineItem.bcd_amount = parseFloat(item.bcdAmount);
      if (hasValue(item.swsAmount) && parseFloat(item.swsAmount) > 0) lineItem.sws_amount = parseFloat(item.swsAmount);
      if (hasValue(item.assessableValue) && parseFloat(item.assessableValue) > 0) lineItem.assessable_value = parseFloat(item.assessableValue);
      if (hasValue(item.taxAmt) && parseFloat(item.taxAmt) > 0) lineItem.tax_amount = parseFloat(item.taxAmt);
      
      // Optional fields - only include if user has filled them
      if (hasValue(item.countryOfOrigin) && item.countryOfOrigin !== 'India') lineItem.country_of_origin = item.countryOfOrigin;
      if (hasValue(item.warranty)) lineItem.warranty_period = item.warranty;
      if (hasValue(item.certifications) && item.certifications.length > 0) lineItem.certifications = item.certifications;
      if (hasValue(item.sampleOffered) && item.sampleOffered !== 'No') {
        lineItem.sample_offered = item.sampleOffered;
        if (hasValue(item.sampleType)) lineItem.sample_type = item.sampleType;
        if (hasValue(item.sampleQuantity) && parseFloat(item.sampleQuantity) > 0) {
          lineItem.sample_quantity = parseFloat(item.sampleQuantity);
          if (hasValue(item.sampleUnit)) lineItem.sample_unit = item.sampleUnit;
        }
        if (hasValue(item.sampleCost) && parseFloat(item.sampleCost) > 0) lineItem.sample_cost = parseFloat(item.sampleCost);
      }
      if (hasValue(item.dispatchLeadTime) && parseInt(item.dispatchLeadTime) > 0) lineItem.dispatch_lead_time = parseInt(item.dispatchLeadTime);
      if (hasValue(item.returnPolicy) && item.returnPolicy !== 'Non-Returnable') lineItem.return_policy = item.returnPolicy;
      if (hasValue(item.bulkPricing) && item.bulkPricing.length > 0) lineItem.bulk_pricing = item.bulkPricing;

      return lineItem;
    });

    // Map payment terms - only include if user has added them
    const paymentTerms = (quotation.paymentTerms && quotation.paymentTerms.length > 0) ? quotation.paymentTerms : [];

    const payload = {
      rfq_id: rfqId,
      quotation_number: quotation.quotationId,
      quote_status: status,
      quote_date: quotation.quoteDate,
      validity_days: hasValue(quotation.validityDays) ? parseInt(quotation.validityDays) : null,
      trade_type: quotation.tradeType,
      currency: hasValue(quotation.currency) ? quotation.currency : null,
      declaration_accepted: quotation.declarationAccepted || false,
      items: lineItems
    };

    // Only include optional fields if they have values
    if (hasValue(quotation.exportTaxType)) payload.export_tax_type = quotation.exportTaxType;
    if (hasValue(quotation.igstRegistrationNumber)) payload.igst_registration_number = quotation.igstRegistrationNumber;
    if (hasValue(quotation.arnNumber)) payload.arn_number = quotation.arnNumber;
    if (hasValue(quotation.paymentMethod)) payload.payment_method = quotation.paymentMethod;
    if (paymentTerms.length > 0) payload.payment_terms = paymentTerms;
    if (hasValue(quotation.tradeTerms)) payload.trade_terms = quotation.tradeTerms;
    if (hasValue(quotation.paymentDueDays)) payload.payment_due_days = parseInt(quotation.paymentDueDays);
    if (hasValue(quotation.advancePayment)) payload.advance_payment = quotation.advancePayment;
    if (hasValue(quotation.balancePayment)) payload.balance_payment = quotation.balancePayment;
    if (hasValue(quotation.shippingAddress)) payload.shipping_address = quotation.shippingAddress;
    if (hasValue(quotation.shippingCountry)) payload.shipping_country = quotation.shippingCountry;
    if (hasValue(quotation.shippingState)) payload.shipping_state = quotation.shippingState;
    if (hasValue(quotation.shippingCity)) payload.shipping_city = quotation.shippingCity;
    if (hasValue(quotation.shippingPincode)) payload.shipping_pincode = quotation.shippingPincode;
    if (hasValue(quotation.shippingMethod)) payload.shipping_method = quotation.shippingMethod;
    if (hasValue(quotation.sellerShippingMode)) payload.seller_shipping_mode = quotation.sellerShippingMode;
    if (hasValue(quotation.destinationPort)) payload.destination_port = quotation.destinationPort;
    if (hasValue(quotation.leadTimeDays)) payload.lead_time_days = parseInt(quotation.leadTimeDays);
    if (hasValue(quotation.deliveryTerms?.deliveryTime)) payload.delivery_time = quotation.deliveryTerms.deliveryTime;
    if (hasValue(quotation.deliveryTerms?.incoterms)) payload.incoterms = quotation.deliveryTerms.incoterms;
    if (hasValue(quotation.deliveryTerms?.warranty)) payload.warranty = quotation.deliveryTerms.warranty;
    if (hasValue(quotation.freightCharges)) payload.freight_charges = parseFloat(quotation.freightCharges);
    if (hasValue(quotation.insuranceCharges)) payload.insurance_charges = parseFloat(quotation.insuranceCharges);
    if (hasValue(quotation.handlingClearance)) payload.handling_clearance = parseFloat(quotation.handlingClearance);

    return payload;
  };

  // Function to upload attachments
  const uploadAttachments = async () => {
    const uploadedAttachments = [];
    
    if (!attachedFiles || attachedFiles.length === 0) {
      console.log('No files to upload');
      return uploadedAttachments;
    }

    console.log(`Starting upload of ${attachedFiles.length} file(s)...`);
    
    for (const file of attachedFiles) {
      try {
        console.log(`Uploading file: ${file.name} (${file.size} bytes, type: ${file.type})`);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'quotation');
        formData.append('companyName', 'Quotation');
        formData.append('fileType', 'quotation_attachment');

        // Log FormData contents for debugging
        console.log('FormData entries:');
        for (const [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
          } else {
            console.log(`  ${key}: ${value}`);
          }
        }

        const uploadResponse = await CommonService.post('/upload/single', formData);
        
        console.log('Upload response:', uploadResponse);
        
        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          const accessPath = uploadResponse.data.accessPath || uploadResponse.data.filePath || '';
          if (accessPath) {
            console.log(`✅ File uploaded successfully: ${file.name} -> ${accessPath}`);
            uploadedAttachments.push(accessPath);
          } else {
            console.warn(`⚠️ Upload succeeded but no accessPath returned for: ${file.name}`);
            toast.warning(`Upload succeeded but path not returned for ${file.name}`);
          }
        } else {
          console.error('❌ Upload response missing success or data:', uploadResponse);
          toast.error(`Upload failed for ${file.name}: Invalid response`);
        }
      } catch (error) {
        console.error(`❌ Error uploading file ${file.name}:`, error);
        console.error('Error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          statusText: error?.response?.statusText
        });
        
        const errorMessage = error?.response?.data?.message || 
                           error?.response?.data?.error || 
                           error?.message || 
                           'Unknown error';
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
      }
    }
    
    console.log(`Upload complete. ${uploadedAttachments.length}/${attachedFiles.length} files uploaded successfully.`);
    return uploadedAttachments;
  };

  const persistQuotation = async (status) => {
    const uploadedAttachments = await uploadAttachments();
    const mergedAttachments = [...existingAttachments, ...uploadedAttachments];
    const payload = prepareQuotationPayload(status);
    payload.attachments = mergedAttachments;

    const response = quotationDbId
      ? await CommonService.put(`/quotation/${quotationDbId}`, payload)
      : await CommonService.post('/quotation', payload);

    if (response?.success) {
      setExistingAttachments(mergedAttachments);
      setAttachedFiles([]);
    }

    return response;
  };

  // Function to save quotation as draft
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      
      const validationErrors = validateQuotation();
      if (validationErrors.length > 0) {
        // Display each error separately for better visibility
        validationErrors.forEach((error, index) => {
          setTimeout(() => {
            toast.error(error, {
              position: "top-right",
              autoClose: 5000,
            });
          }, index * 100); // Stagger errors slightly
        });
        // Also show a summary
        toast.error(`Please fix ${validationErrors.length} validation error(s)`, {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      // Use the selected quote status, default to 'Draft' if not set
      const quoteStatus = quotationDbId ? 'Draft' : (quotation.quoteStatus || 'Draft');
      const response = await persistQuotation(quoteStatus);
      
      console.log('Submitting quotation draft with payload status:', quoteStatus);
      
      if (response && response.success) {
        // Store the quotation database ID for conversations
        if (response.data && response.data.quotationId) {
          setQuotationDbId(response.data.quotationId);
          // Fetch conversations after saving draft (user stays on page)
          try {
            await fetchConversations(response.data.quotationId);
          } catch (error) {
            // Silently fail - conversations will load when needed
            console.warn('Could not fetch conversations after saving draft:', error);
          }
        }
        toast.success('Quotation saved as draft successfully!');
      } else {
        throw new Error(response?.message || 'Failed to save quotation');
      }
    } catch (error) {
      console.error('Error saving quotation draft:', error);
      console.error('Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        method: error?.config?.method
      });
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to save quotation. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Function to submit quotation
  const handleSubmitQuotation = async (e) => {
    // Prevent default form submission if called from form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('=== SUBMIT QUOTATION CLICKED ===');
    
    // Validate all mandatory fields BEFORE setting submitting state
    const validationErrors = validateQuotation();
    
    if (validationErrors.length > 0) {
      console.error('🚫 BLOCKING SUBMISSION - Validation errors found:', validationErrors);
      
      // Display first error immediately
      toast.error(validationErrors[0], {
        position: "top-center",
        autoClose: 4000,
      });
      
      // Display remaining errors with slight delay
      if (validationErrors.length > 1) {
        validationErrors.slice(1).forEach((error, index) => {
          setTimeout(() => {
            toast.error(error, {
              position: "top-right",
              autoClose: 5000,
            });
          }, (index + 1) * 200);
        });
      }
      
      // Show summary
      setTimeout(() => {
        toast.error(`Please fix ${validationErrors.length} validation error(s) before submitting`, {
          position: "top-center",
          autoClose: 4000,
        });
      }, validationErrors.length * 200 + 500);
      
      return false; // Stop submission if validation fails - DO NOT proceed
    }

    // Check if declaration is accepted
    if (!quotation.declarationAccepted) {
      console.error('🚫 BLOCKING SUBMISSION - Declaration not accepted');
      toast.error('Please accept the declaration before submitting', {
        position: "top-center",
        autoClose: 4000,
      });
      return false; // Stop submission if declaration is not accepted
    }

    console.log('✅ Validation passed - Proceeding with submission');

    try {
      setIsSubmitting(true);

      // Submit always uses Submitted status (triggers approval when applicable)
      const response = await persistQuotation('Submitted');
      
      if (response && response.success) {
        // Store the quotation database ID for conversations (will be used if user stays on page)
        if (response.data && response.data.quotationId) {
          setQuotationDbId(response.data.quotationId);
          // Don't fetch conversations here - we're navigating away immediately
          // Conversations will be loaded when user views the quotation later
        }
        toast.success('Quotation submitted successfully!');
        // Navigate immediately to prevent any race conditions with API calls
        // Use a very short delay just to ensure toast is visible
        setTimeout(() => {
          console.log('Navigating to seller-rfq-list with activeTab: quoted');
          // Navigate to seller-rfq-list with activeTab state
          // The API interceptor will not redirect if we're on seller-rfq-list
          navigate('/seller-rfq-list', { 
            state: { activeTab: 'quoted' },
            replace: false
          });
        }, 100);
      } else {
        throw new Error(response?.message || 'Failed to submit quotation');
      }
    } catch (error) {
      console.error('Error submitting quotation:', error);
      console.error('Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        method: error?.config?.method
      });
      
      // Handle 401 Unauthorized - don't navigate, let user see the error
      // The API interceptor will handle the redirect, but we should prevent navigation
      if (error?.response?.status === 401) {
        toast.error('Your session has expired. Please login again.');
        // Don't navigate - let the API interceptor handle the redirect
        setIsSubmitting(false);
        return;
      }
      
      // Handle backend validation errors
      let errorMessage = 'Failed to submit quotation. Please try again.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
        // If backend returns validation errors, display them
        if (errorMessage.includes('Validation failed:')) {
          const validationErrors = errorMessage.split('Validation failed:')[1]?.split(';') || [];
          validationErrors.forEach((err, index) => {
            if (err.trim()) {
              setTimeout(() => {
                toast.error(err.trim(), {
                  position: "top-right",
                  autoClose: 5000,
                });
              }, index * 200);
            }
          });
        } else {
          toast.error(errorMessage, {
            position: "top-center",
            autoClose: 5000,
          });
        }
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else if (error?.message) {
        errorMessage = error.message;
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="submit-quotation">
      <div className="container-fluid">
        {/* Header */}
        <div className="sq-header">
          <div className="sq-header-content">
            <div>
              <h1 className="sq-title">{isEditingDraft ? 'Edit Draft Quotation' : 'Seller Quotation Response'}</h1>
              <p className="sq-subtitle">
                {isEditingDraft
                  ? 'Update your saved draft and submit to the buyer'
                  : "Respond to buyer's RFQ with your quotation"}
              </p>
            </div>
            <div className="sq-header-actions">
              <button className="btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
              <button 
                className="btn-secondary" 
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isSubmitting}
              >
                {isSavingDraft ? 'Saving...' : (quotationDbId ? 'Update Draft' : 'Save as Draft')}
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSubmitQuotation}
                disabled={isSubmitting || isSavingDraft || !isFormValid()}
                title={!isFormValid() ? 'Please fill all mandatory fields and accept the declaration' : ''}
              >
                {isSubmitting ? 'Submitting...' : (isEditingDraft ? 'Resubmit Quote' : 'Submit Quote')}
              </button>
            </div>
          </div>
        </div>

        {/* Buyer's RFQ Section Header - Above RFQ Details */}
        <div className="sq-rfq-header-section">
          <div className="sq-rfq-header-wrapper">
            <div className="sq-rfq-header-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-rfq-header-icon">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                <path d="M10 9H8"></path>
                <path d="M16 13H8"></path>
                <path d="M16 17H8"></path>
              </svg>
              <h2 className="sq-rfq-header-text">Buyer's Request for Quotation (RFQ)</h2>
            </div>
            <button className="btn-readonly">Read Only</button>
          </div>
        </div>

        {/* RFQ Details - Standalone */}
        <div className="sq-rfq-details-standalone">
          <div className="sq-rfq-details-card">
            <div className="sq-rfq-details-header" onClick={() => setRfqDetailsExpanded(!rfqDetailsExpanded)}>
              <div className="sq-rfq-details-header-left">
                <h3 className="sq-section-title">RFQ Details</h3>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className={`sq-rfq-details-chevron ${rfqDetailsExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
              >
                <path d="m18 15-6-6-6 6"></path>
              </svg>
            </div>
            {rfqDetailsExpanded && (
              <div className="sq-rfq-details-content">
                <div className="sq-rfq-details-grid">
                  <div className="sq-rfq-detail-item">
                    <label>RFQ ID</label>
                    <p className="sq-value">{rfqData.rfqId}</p>
                  </div>
                  <div className="sq-rfq-detail-item">
                    <label>RFQ Date</label>
                    <p className="sq-value">{rfqData.rfqDate}</p>
                  </div>
                  <div className="sq-rfq-detail-item">
                    <label>RFQ Status</label>
                    <div>
                      <span className="sq-status sq-status-open">{rfqData.rfqStatus}</span>
                    </div>
                  </div>
                  <div className="sq-rfq-detail-item">
                    <label>Required By Date</label>
                    <p className="sq-value">{rfqData.requiredByDate}</p>
                  </div>
                  <div className="sq-rfq-detail-item">
                    <label>Payment Terms</label>
                    <p className="sq-value">{rfqData.paymentTerms}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Buyer Information - Standalone */}
        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setBuyerInfoExpanded(!buyerInfoExpanded)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                  <path d="M10 6h4"></path>
                  <path d="M10 10h4"></path>
                  <path d="M10 14h4"></path>
                  <path d="M10 18h4"></path>
                </svg>
                <h3 className="sq-section-title">Buyer Information</h3>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className={`sq-buyer-info-chevron ${buyerInfoExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
              >
                <path d="m18 15-6-6-6 6"></path>
              </svg>
            </div>
            {buyerInfoExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid">
                  <div className="sq-buyer-info-item">
                    <label>Company Name</label>
                    <p className="sq-value">{rfqData.buyer.companyName}</p>
                  </div>
                  <div className="sq-buyer-info-item">
                    <label>Contact Person</label>
                    <p className="sq-value">{rfqData.buyer.contactPerson}</p>
                  </div>
                  <div className="sq-buyer-info-item">
                    <label>Buyer Rating</label>
                    <div className="sq-rating">
                      <StarFilled className="sq-star-icon" />
                      <span className="sq-rating-value">{rfqData.buyer.rating}</span>
                      <span className="sq-rating-divider">/5.0</span>
                    </div>
                  </div>
                  <div className="sq-buyer-info-item">
                    <label>Email</label>
                    <p className="sq-value">{rfqData.buyer.email}</p>
                  </div>
                  <div className="sq-buyer-info-item">
                    <label>Contact Number</label>
                    <div className="sq-contact-number">
                      <PhoneOutlined className="sq-phone-icon" />
                      <span className="sq-phone-value">{rfqData.buyer.contactNumber}</span>
                    </div>
                  </div>
                  <div className="sq-buyer-info-item">
                    <label>Delivery Address</label>
                    <p className="sq-value">
                      {[
                        rfqData.buyer.deliveryAddress,
                        rfqData.buyer.deliveryCity,
                        rfqData.buyer.deliveryState,
                        rfqData.buyer.deliveryCountry,
                        rfqData.buyer.deliveryPincode ? `PIN: ${rfqData.buyer.deliveryPincode}` : null
                      ].filter(Boolean).join(', ') || '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Items Requested - Standalone */}
        <div className="sq-items-requested-standalone">
          <div className="sq-items-requested-card">
            <div className="sq-items-requested-header" onClick={() => setItemsRequestedExpanded(!itemsRequestedExpanded)}>
              <div className="sq-items-requested-header-left">
                <h3 className="sq-section-title">Items Requested by Buyer</h3>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className={`sq-items-requested-chevron ${itemsRequestedExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
              >
                <path d="m18 15-6-6-6 6"></path>
              </svg>
            </div>
            {itemsRequestedExpanded && rfqData.items.length > 0 && (
              <div className="sq-items-requested-content">
                <div className="sq-items-requested-grid sq-items-requested-row-1">
                  <div className="sq-items-requested-item">
                    <label>Item Name</label>
                    <p className="sq-value">{rfqData.items[0].description}</p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Quantity</label>
                    <p className="sq-value">{rfqData.items[0].quantity} {rfqData.items[0].uom}</p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Target Price Range</label>
                    <p className="sq-value">{rfqData.items[0].targetPriceRange || '-'}</p>
                  </div>
                </div>
                <div className="sq-items-requested-grid sq-items-requested-row-2">
                  <div className="sq-items-requested-item">
                    <label>Procurement Method</label>
                    <p className="sq-value">{rfqData.items[0].procurementMethod || '-'}</p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Payment Term</label>
                    <p className="sq-value">{rfqData.paymentTerms || '-'}</p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Incoterm</label>
                    <p className="sq-value">{rfqData.tradeTerms || '-'}</p>
                  </div>
                </div>
                <div className="sq-items-requested-grid sq-items-requested-row-3">
                  <div className="sq-items-requested-item">
                    <label>Shipping Address</label>
                    <p className="sq-value">
                      {[
                        rfqData.shippingAddress,
                        rfqData.shippingCity,
                        rfqData.shippingState,
                        rfqData.shippingCountry,
                        rfqData.shippingPincode ? `PIN: ${rfqData.shippingPincode}` : null
                      ].filter(Boolean).join(', ') || '-'}
                    </p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Preferred Shipping Method</label>
                    <p className="sq-value">{rfqData.shippingMethod || '-'}</p>
                  </div>
                  <div className="sq-items-requested-item">
                    <label>Shipping Days</label>
                    <p className="sq-value">{rfqData.leadTimeDays || '-'} days</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Special Requirements - Standalone */}
        <div className="sq-special-requirements-standalone">
          <div className="sq-special-requirements">
            <div className="sq-special-requirements-header" onClick={() => setSpecialRequirementsExpanded(!specialRequirementsExpanded)}>
              <div className="sq-special-requirements-header-left">
                <h3 className="sq-section-title">Special Requirements & Notes</h3>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className={`sq-special-requirements-chevron ${specialRequirementsExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
              >
                <path d="m18 15-6-6-6 6"></path>
              </svg>
            </div>
            {specialRequirementsExpanded && (
              <p>{rfqData.specialRequirements}</p>
            )}
          </div>
        </div>

        {/* Your Quotation Response Header - Above Card */}
        <div className="sq-quotation-response-header">
          <div className="sq-card-title-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-icon sq-file-icon" aria-hidden="true">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
            <h2 className="sq-card-title">Your Quotation Response</h2>
          </div>
          <span className="sq-badge sq-badge-editable">Editable</span>
        </div>

        {/* Your Quotation Response Section - Editable */}
        <div>
            {/* Quotation Information */}
            <div className="sq-section sq-quotation-info-section">
              <h3 className="sq-section-title">Quotation Information</h3>
              <div className="sq-grid-4">
                <div className="sq-field">
                  <label>Quotation ID</label>
                  <input
                    type="text"
                    className="input-control"
                    value={quotation.quotationId || generateQuotationId()}
                    readOnly
                    placeholder="Auto-generated"
                    title="Quotation ID is auto-generated"
                  />
                </div>
                <div className="sq-field">
                  <label>Quote Status <span className="sq-required">*</span></label>
                  <select
                    className="input-control input-control-select"
                    value={quotation.quoteStatus || ''}
                    onChange={(e) => setQuotation(prev => ({ ...prev, quoteStatus: e.target.value }))}
                    required
                  >
                    <option value="" disabled>Select Quote Status</option>
                    <option value="Draft">Draft</option>
                    <option value="Submitted">Submitted</option>
                  </select>
                  <span className="sq-helper-text">Note: Accepted and Rejected statuses are set by the buyer</span>
                </div>
                <div className="sq-field">
                  <label>Quote Date <span className="sq-required">*</span></label>
                  <input
                    type="date"
                    className="input-control"
                    value={quotation.quoteDate}
                    onChange={(e) => setQuotation(prev => ({ ...prev, quoteDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="sq-field">
                  <label>Validity (Days) <span className="sq-required">*</span></label>
                  <input
                    type="number"
                    className="input-control"
                    value={quotation.validityDays || ''}
                    onChange={(e) => setQuotation(prev => ({ ...prev, validityDays: e.target.value }))}
                    placeholder="Enter validity days"
                    min="1"
                    required
                  />
                </div>
              </div>

              {/* Trade Type & Tax Configuration */}
              <div className="sq-trade-tax-subsection">
                <h3 className="sq-subsection-title">Trade Type & Tax Configuration</h3>
                <div className={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' ? 'sq-grid-4' : 'sq-grid-2'}>
                  <div className="sq-field">
                    <label>Currency <span className="sq-required">*</span></label>
                    <select
                      className="input-control input-control-select"
                      value={quotation.currency || ''}
                      onChange={(e) => handleShippingChange('currency', e.target.value)}
                      required
                      disabled={currencyLoading}
                    >
                      <option value="" disabled>
                        {currencyLoading ? 'Loading currencies...' : 'Select Currency'}
                      </option>
                      {currencyOptions && currencyOptions.length > 0 && (
                        currencyOptions.map((opt, idx) => (
                          <option key={`currency-${opt.value || opt.currencyCode}-${idx}`} value={opt.value || opt.currencyCode}>
                            {opt.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="sq-field">
                    <label>Trade Type <span className="sq-required">*</span></label>
                    <select
                      className="input-control input-control-select"
                      value={quotation.tradeType}
                      disabled
                      title="Trade Type is automatically determined based on Buyer and Seller locations"
                    >
                      <option value="IN India to India - Intra-State (Same State)">IN India to India - Intra-State (Same State)</option>
                      <option value="IN India to India - Inter-State (Different States)">IN India to India - Inter-State (Different States)</option>
                      <option value="🌎 →IN Import into India (Foreign → India)">🌎 →IN Import into India (Foreign → India)</option>
                      <option value="IN → 🌎 Export from India (India → Foreign)">IN → 🌎 Export from India (India → Foreign)</option>
                      <option value="🌎 Third Country Trade (Foreign → Foreign)">🌎 Third Country Trade (Foreign → Foreign)</option>
                    </select>
                  </div>
                  {quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && (
                    <>
                      <div className="sq-field">
                        <label>Export Tax Type <span className="sq-required">*</span></label>
                        <select
                          className="input-control input-control-select"
                          value={quotation.exportTaxType || ''}
                          onChange={(e) => setQuotation(prev => ({ ...prev, exportTaxType: e.target.value }))}
                        >
                          <option value="" disabled>Select Export Tax Type</option>
                          <option value="LUT (Letter of Undertaking) - 0% IGST">LUT (Letter of Undertaking) - 0% IGST</option>
                          <option value="Non-LUT - IGST Applicable">Non-LUT - IGST Applicable</option>
                        </select>
                      </div>
                      {quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? (
                        <div className="sq-field">
                          <label>ARN Number (Application Reference Number) <span className="sq-required">*</span></label>
                          <input
                            type="text"
                            className="input-control"
                            value={quotation.arnNumber || ''}
                            onChange={(e) => setQuotation(prev => ({ ...prev, arnNumber: e.target.value }))}
                            placeholder="Enter ARN number"
                            required
                          />
                          <span className="sq-helper-text">ARN for LUT approved by GST department</span>
                        </div>
                      ) : (
                        <div className="sq-field">
                          <label>IGST Registration Number (GST Number) <span className="sq-required">*</span></label>
                          <input
                            type="text"
                            className="input-control"
                            value={quotation.igstRegistrationNumber || ''}
                            onChange={(e) => setQuotation(prev => ({ ...prev, igstRegistrationNumber: e.target.value }))}
                            placeholder="Enter 15-digit GST number"
                            required
                          />
                          <span className="sq-helper-text">Your 15-digit GST identification number for IGST export</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Information Box for Intra-State Trade */}
                {quotation.tradeType === 'IN India to India - Intra-State (Same State)' && (
                  <div className="sq-info-box">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="sq-info-icon"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    <div className="sq-info-content">
                      <strong>Intra-State Trade:</strong> CGST (9%) + SGST (9%) will apply. Both buyer and seller are in the same state.
                    </div>
                  </div>
                )}

                {/* Information Box for Inter-State Trade */}
                {quotation.tradeType === 'IN India to India - Inter-State (Different States)' && (
                  <div className="sq-info-box sq-info-box-indigo">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="sq-info-icon"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    <div className="sq-info-content">
                      <strong>Inter-State Trade:</strong> IGST (18%) will apply. Buyer and seller are in different Indian states.
                    </div>
                  </div>
                )}

                {/* Information Box for Import to India */}
                {quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                  <div className="sq-info-box sq-info-box-purple">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="sq-info-icon"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    <div className="sq-info-content">
                      <strong>Import into India:</strong> CIF calculation + BCD (Basic Customs Duty) + SWS (Social Welfare Surcharge) + IGST will apply as per Indian customs regulations.
                    </div>
                  </div>
                )}

                {/* Success Box for LUT Export */}
                {quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' && (
                  <div className="sq-success-box">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="sq-success-icon"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <div className="sq-success-content">
                      <strong>LUT Export:</strong> No IGST will be charged. Ensure ARN number is valid and LUT is active.
                    </div>
                  </div>
                )}

                {/* Warning Box for Non-LUT Export */}
                {quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'Non-LUT - IGST Applicable' && (
                  <div className="sq-warning-box">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="sq-warning-icon"
                    >
                      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                      <path d="M12 9v4"></path>
                      <path d="M12 17h.01"></path>
                    </svg>
                    <div className="sq-warning-content">
                      <strong>Non-LUT Export:</strong> IGST will be charged on the invoice value using GST Number: {quotation.igstRegistrationNumber}. You can claim refund after export with shipping bills and proper documentation.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items Section */}
            {(quotation.tradeType === 'IN India to India - Intra-State (Same State)' || 
              quotation.tradeType === 'IN India to India - Inter-State (Different States)' || 
              quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' || 
              quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ||
              quotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)') && (
              <div className="sq-section sq-line-items-section">
                <div className="sq-line-items-header">
                  <div className="sq-line-items-title-wrapper">
                    <h3 className="sq-section-title">
                      Line Items
                      {!(quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST') && 
                       quotation.tradeType !== '🌎 Third Country Trade (Foreign → Foreign)' && (
                        <span className="sq-badge sq-badge-purple">
                          {quotation.tradeType === 'IN India to India - Intra-State (Same State)' 
                            ? 'CGST, SGST & CESS Enabled ✓' 
                            : 'IGST & CESS Enabled ✓'}
                        </span>
                      )}
                    </h3>
                    <p className="sq-help-text">Review and edit items from quotation. Add additional items like packaging, installation, or freight charges as needed</p>
                  </div>
                  <button className="btn-secondary sq-add-item-btn" onClick={addLineItem}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14"></path>
                      <path d="M12 5v14"></path>
                    </svg>
                    Add Line Item
                  </button>
                </div>
                
                <div className="sq-table-wrapper">
                  <table className="sq-table sq-table-domestic">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description <span className="sq-required">*</span></th>
                        <th>HSN/SAC <span className="sq-required">*</span></th>
                        <th>Qty <span className="sq-required">*</span></th>
                        <th>UoM <span className="sq-required">*</span></th>
                        <th>Unit Price ({getCurrencySymbol()}) <span className="sq-required">*</span></th>
                        <th>Disc %</th>
                        {quotation.tradeType === 'IN India to India - Intra-State (Same State)' ? (
                          <>
                            <th className="sq-bg-orange">CGST %</th>
                            <th className="sq-bg-purple">SGST %</th>
                            <th className="sq-bg-purple">CESS %</th>
                          </>
                        ) : quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ? (
                          <>
                            <th className="sq-bg-orange">Customs %</th>
                            <th className="sq-bg-orange">IGST %</th>
                            <th className="sq-bg-purple">CESS %</th>
                          </>
                        ) : quotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' ? (
                          <>
                            <th>Tax %</th>
                          </>
                        ) : (
                          <>
                            <th className="sq-bg-orange">IGST %</th>
                            <th className="sq-bg-purple">CESS %</th>
                          </>
                        )}
                        <th>Total ({getCurrencySymbol()})</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotation.items.map(item => {
                        return (
                        <tr key={item.id} className="sq-row-highlight">
                          <td>{item.id}</td>
                          <td className="sq-bg-yellow">
                              <input
                                type="text"
                                className="input-control sq-input-full"
                                value={item.description || ''}
                                onChange={(e) => handleQuotationChange(item.id, 'description', e.target.value)}
                              placeholder="Enter description *"
                              required
                              />
                          </td>
                          <td className="sq-bg-yellow">
                              <input
                                type="text"
                                className="input-control sq-input-hsn"
                                value={item.hsnCode || ''}
                                onChange={(e) => handleQuotationChange(item.id, 'hsnCode', e.target.value)}
                              placeholder="Enter HSN/SAC *"
                              required
                              />
                          </td>
                          <td>
                              <input
                                type="number"
                                className="input-control sq-input-qty"
                                value={item.quantity}
                                onChange={(e) => handleQuotationChange(item.id, 'quantity', e.target.value)}
                              min="0.01"
                              step="0.01"
                              required
                              />
                          </td>
                          <td>
                              <select
                              className="sq-input-uom input-control input-control-select "
                                value={item.uom || ''}
                                onChange={(e) => handleQuotationChange(item.id, 'uom', e.target.value)}
                              required
                              disabled={uomLoading}
                            >
                              <option value="" disabled>
                                {uomLoading ? 'Loading UoM...' : 'Select UoM *'}
                              </option>
                              {attributeUnitOptions && attributeUnitOptions.length > 0 ? (
                                attributeUnitOptions.map((opt, idx) => (
                                  <option key={`uom-${opt.value}-${idx}`} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))
                              ) : !uomLoading ? (
                                <option value="" disabled>No UoM units available</option>
                              ) : null}
                              </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input-control sq-input-price"
                              value={item.unitPrice}
                              onChange={(e) => handleQuotationChange(item.id, 'unitPrice', e.target.value)}
                              min="0.01"
                              step="0.01"
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input-control sq-input-price"
                              value={item.discount || ''}
                              onChange={(e) => handleQuotationChange(item.id, 'discount', e.target.value)}
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0"
                            />
                          </td>
                          {quotation.tradeType === 'IN India to India - Intra-State (Same State)' ? (
                            <>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.igst || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'igst', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.sgst || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'sgst', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.cess || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'cess', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                            </>
                          ) : quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ? (
                            <>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.customs || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'customs', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.igst || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'igst', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.cess || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'cess', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                            </>
                          ) : quotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' ? (
                            <>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.tax || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'tax', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0 *"
                                  required
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.igst || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'igst', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? '0' : '0 *'}
                                  readOnly={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST'}
                                  required={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'Non-LUT - IGST Applicable'}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="input-control sq-input-price"
                                  value={item.cess || ''}
                                  onChange={(e) => handleQuotationChange(item.id, 'cess', e.target.value)}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? '0' : '0 *'}
                                  readOnly={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST'}
                                  required={quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'Non-LUT - IGST Applicable'}
                                />
                              </td>
                            </>
                          )}
                          <td className="sq-readonly-cell">{formatCurrency(item.total || 0)}</td>
                          <td>
                            <button className="sq-btn-remove" onClick={() => removeLineItem(item.id)}>
                              <CloseOutlined />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Summary Section with Payment & Delivery Terms */}
                <div className="sq-domestic-summary">
                  <div className="sq-summary-grid">
                    <div className="sq-summary-left">
                      {/* Payment Terms */}
                      <div className="sq-terms-box">
                        <h4 className="sq-terms-title">Payment Terms</h4>
                        
                        <div className="sq-grid-payment-terms-3col">
                          <div className="sq-field">
                            <label>Payment Due Within</label>
                            <input
                              type="number"
                              className="input-control"
                              value={quotation.paymentDueDays || ''}
                              onChange={(e) => handleShippingChange('paymentDueDays', e.target.value)}
                              placeholder="30 days"
                              min="0"
                            />
                          </div>
                          <div className="sq-field">
                            <label>Advance Payment (%)</label>
                            <input
                              type="number"
                              className="input-control"
                              value={quotation.advancePayment || ''}
                              onChange={(e) => handleShippingChange('advancePayment', e.target.value)}
                              placeholder="30%"
                              min="0"
                              max="100"
                            />
                          </div>
                          <div className="sq-field">
                            <label>Balance Payment (%)</label>
                            <input
                              type="number"
                              className="input-control"
                              value={quotation.balancePayment || ''}
                              onChange={(e) => handleShippingChange('balancePayment', e.target.value)}
                              placeholder="70%"
                              min="0"
                              max="100"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delivery Terms */}
                      <div className="sq-terms-box sq-mt-16">
                        <h4 className="sq-terms-title">Delivery Terms</h4>
                        
                        <div className="sq-grid-delivery-terms">
                          <div className="sq-field">
                            <label>Trade Terms (Incoterms)</label>
                            <select
                              className="input-control input-control-select"
                              value={quotation.tradeTerms || ''}
                              onChange={(e) => handleShippingChange('tradeTerms', e.target.value)}
                              disabled={incotermLoading}
                            >
                              <option value="" disabled>
                                {incotermLoading ? 'Loading incoterms...' : 'Select Incoterms'}
                              </option>
                              {shippingTermsOptions && shippingTermsOptions.length > 0 && (
                                shippingTermsOptions.map((opt, idx) => (
                                  <option key={`incoterm-${opt.value}-${idx}`} value={opt.label}>
                                    {opt.label}
                                  </option>
                                ))
                              )}
                            </select>
                          </div>
                          <div className="sq-field">
                            <label>Delivery Time</label>
                            <div className="sq-flex-delivery-time">
                              <input
                                type="number"
                                className="input-control sq-input-delivery-number"
                                value={parseDeliveryTime(quotation.deliveryTerms.deliveryTime || '').value || ''}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const parsed = parseDeliveryTime(quotation.deliveryTerms.deliveryTime || '');
                                  const newDeliveryTime = formatDeliveryTime(inputValue, parsed.unit);
                                  handleDeliveryTermsChange('deliveryTime', newDeliveryTime);
                                }}
                                placeholder="4"
                                min="0"
                              />
                              <select
                                className="input-control input-control-select sq-input-delivery-select"
                                value={parseDeliveryTime(quotation.deliveryTerms.deliveryTime || '').unit || ''}
                                onChange={(e) => {
                                  const parsed = parseDeliveryTime(quotation.deliveryTerms.deliveryTime || '');
                                  const newDeliveryTime = formatDeliveryTime(parsed.value, e.target.value);
                                  handleDeliveryTermsChange('deliveryTime', newDeliveryTime);
                                }}
                              >
                                <option value="" disabled>Select Delivery Time Unit</option>
                                {Object.entries(DELIVERY_TIME_UNITS).map(([key, value]) => (
                                  <option key={key} value={key}>{value}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="sq-terms-box sq-mt-16">
                        <h4 className="sq-terms-title">Shipping Arrangement</h4>
                        <div className="sq-grid-2">
                          <div className="sq-field">
                            <label htmlFor="sellerShippingMode">Who arranges transport?</label>
                            <select
                              id="sellerShippingMode"
                              className="input-control input-control-select"
                              value={quotation.sellerShippingMode || 'SELF_SHIPPING'}
                              onChange={(e) => handleShippingChange('sellerShippingMode', e.target.value)}
                            >
                              <option value="SELF_SHIPPING">Self shipping (seller delivers)</option>
                              <option value="THIRD_PARTY_LOGISTICS">Third-party logistics (seller arranges carrier)</option>
                              <option value="BUYER_PICKUP">Buyer pickup</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Import Charges (CIF Calculation) - Only for Import */}
                      {quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                        <div className="sq-terms-box sq-mt-16">
                          <h4 className="sq-terms-title">Import Charges (CIF Calculation)</h4>
                          
                          <div className="sq-grid-2">
                            <div className="sq-field">
                              <label>Freight Charges (% of FOB)</label>
                              <input
                                type="number"
                                className="input-control"
                                value={quotation.freightCharges || ''}
                                onChange={(e) => {
                                  handleShippingChange('freightCharges', e.target.value);
                                }}
                                placeholder="Enter freight charges percentage"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div className="sq-field">
                              <label>Insurance Charges (% of FOB)</label>
                              <input
                                type="number"
                                className="input-control"
                                value={quotation.insuranceCharges || ''}
                                onChange={(e) => {
                                  handleShippingChange('insuranceCharges', e.target.value);
                                }}
                                placeholder="Enter insurance charges percentage"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>
                          <div className="sq-field sq-handling-clearance-field">
                            <label>Handling & Clearance (₹)</label>
                            <input
                              type="number"
                              className="input-control"
                              value={quotation.handlingClearance || ''}
                              onChange={(e) => {
                                handleShippingChange('handlingClearance', e.target.value);
                              }}
                              placeholder="Enter handling & clearance amount"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="sq-summary-right">
                      <div className="sq-summary-content">
                        <div className="sq-summary-row sq-discount-row">
                          <span className="sq-summary-label">Total Discount:</span>
                          <span className="sq-summary-value sq-text-red">- {formatCurrency(getDomesticTotals().discountAmount)}</span>
                        </div>
                        {quotation.tradeType === 'IN India to India - Intra-State (Same State)' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">Taxable Amount:</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-igst-row">
                              <span className="sq-summary-label">CGST:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-cess-row">
                              <span className="sq-summary-label">SGST:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().sgstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-cess-row">
                              <span className="sq-summary-label">CESS:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().cessAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : quotation.tradeType === 'IN India to India - Inter-State (Different States)' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">Taxable Amount:</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-igst-row">
                              <span className="sq-summary-label">IGST:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-cess-row">
                              <span className="sq-summary-label">CESS:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().cessAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">FOB Value (Free on Board):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">+ Freight Charges ({quotation.freightCharges || 0}% of FOB):</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().freightAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">+ Insurance Charges ({quotation.insuranceCharges || 0}% of FOB):</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().insuranceAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">+ Handling & Clearance (Fixed):</span>
                              <span className="sq-summary-value">{formatCurrency(quotation.handlingClearance || 0)}</span>
                            </div>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">= CIF Value (Cost + Insurance + Freight):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().cifValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Basic Customs Duty (BCD on CIF):</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().bcdAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Social Welfare Surcharge (10% of BCD):</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().swsAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">= Assessable Value (CIF + BCD + SWS):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().assessableValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-igst-row">
                              <span className="sq-summary-label">IGST (on Assessable Value):</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">FOB Value (Free on Board):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-lut-export-row">
                              <div className="sq-lut-export-content">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="20" 
                                  height="20" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                  className="sq-lut-check-icon"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                <div className="sq-lut-export-text">
                                  <div className="sq-lut-export-title">
                                    Export with LUT (Letter of Undertaking) - 0% IGST
                                  </div>
                                  <div className="sq-lut-arn-text">
                                    ARN: {quotation.arnNumber || 'AA341234567890Z'}
                                  </div>
                                </div>
                              </div>
                              <span className="sq-summary-value sq-lut-no-tax">No Tax</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row sq-lut-grand-total">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : quotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && quotation.exportTaxType === 'Non-LUT - IGST Applicable' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">FOB Value (Free on Board):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">IGST:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">CESS:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().cessAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-non-lut-export-row">
                              <div className="sq-non-lut-export-content">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="20" 
                                  height="20" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                  className="sq-non-lut-warning-icon"
                                >
                                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                  <path d="M12 9v4"></path>
                                  <path d="M12 17h.01"></path>
                                </svg>
                                <span className="sq-non-lut-export-label">Export without LUT - IGST Amount (Refundable):</span>
                              </div>
                              <span className="sq-summary-value sq-non-lut-export-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-non-lut-export-row">
                              <div className="sq-non-lut-export-content">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="20" 
                                  height="20" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                  className="sq-non-lut-warning-icon"
                                >
                                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                  <path d="M12 9v4"></path>
                                  <path d="M12 17h.01"></path>
                                </svg>
                                <span className="sq-non-lut-export-label">Export without LUT - CESS Amount (Refundable):</span>
                              </div>
                              <span className="sq-summary-value sq-non-lut-export-value">{formatCurrency(getDomesticTotals().cessAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : quotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' ? (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">FOB Value (Free on Board):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (Sum of Line Items):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="sq-summary-row sq-fob-row">
                              <span className="sq-summary-label sq-fob-label">FOB Value (Free on Board):</span>
                              <span className="sq-summary-value sq-fob-value">{formatCurrency(getDomesticTotals().fobValue)}</span>
                            </div>
                            <div className="sq-summary-row sq-igst-row">
                              <span className="sq-summary-label">IGST Amount:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().igstAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-cess-row">
                              <span className="sq-summary-label">CESS Amount:</span>
                              <span className="sq-summary-value">{formatCurrency(getDomesticTotals().cessAmount)}</span>
                            </div>
                            <div className="sq-summary-row sq-grand-total-row">
                              <span className="sq-summary-label sq-grand-total-label">Grand Total (FOB + IGST + CESS):</span>
                              <span className="sq-summary-value sq-grand-total-value">{formatCurrency(getDomesticTotals().total)}</span>
                            </div>
                            <div className="sq-summary-row sq-subtotal-row">
                              <span className="sq-summary-label">Subtotal (Qty × Unit Price):</span>
                              <span className="sq-summary-value" >{formatCurrency(getDomesticTotals().subtotal)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Import Calculation Verification Box */}
                {quotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                  <div className="sq-info-box sq-info-box-verification">
                    <div className="sq-info-content">
                      <p>
                        <strong>Import Calculation Verification:</strong>
                      </p>
                      <div>
                        <p>• Subtotal (Σ Qty × Unit Price) = {formatCurrency(getDomesticTotals().subtotal)}</p>
                        <p>• Less: Total Discount = {formatCurrency(getDomesticTotals().discountAmount)}</p>
                        <p>• FOB Value (Subtotal - Discount) = {formatCurrency(getDomesticTotals().fobValue)}</p>
                        <p>✓ Each line item total includes: Item FOB + Proportional (Freight + Insurance + Handling) + BCD + SWS + IGST + CESS</p>
                        <p>✓ Sum of all line item totals = {formatCurrency(getDomesticTotals().total)}</p>
                        <p>✓ Calculation follows Indian Customs Act: BCD on CIF, IGST on Assessable Value (CIF + BCD + SWS)</p>
                      </div>
                    </div>
                  </div>
                )}


                {/* Bulk Pricing Tiers */}
                <div className="sq-bulk-pricing-wrapper">
                  <h3 className="sq-section-title">Bulk Pricing Tiers (Optional)</h3>
                  <p className="sq-help-text">Define volume-based pricing for each product (Maximum 3 tiers)</p>
                  
                  {quotation.items.map(item => {
                    // Ensure bulkPricing exists
                    const bulkPricing = item.bulkPricing || [{ tier: 1, minQty: 1, maxQty: '', unitPrice: 0, discount: 'Base' }];
                    
                    return (
                    <div key={item.id} className="sq-bulk-pricing-item">
                      <div className="sq-bulk-pricing-header" onClick={() => toggleBulkPricing(item.id)}>
                        <div className="sq-bulk-pricing-header-left">
                          <span className="sq-item-badge">Item {item.id}</span>
                          <span className="sq-item-description">{item.description || 'New Item'}</span>
                        </div>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className={`sq-toggle-icon ${expandedBulkPricing[item.id] ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                          aria-hidden="true"
                        >
                          <path d="m18 15-6-6-6 6"></path>
                        </svg>
                      </div>
                      
                      {expandedBulkPricing[item.id] && (
                        <div className="sq-bulk-pricing-content">
                          <div className="sq-table-wrapper">
                            <table className="sq-table">
                              <thead>
                                <tr>
                                  <th>Tier</th>
                                  <th>Min Qty</th>
                                  <th>Max Qty</th>
                                  <th>Unit Price ({getCurrencySymbol()})</th>
                                  <th className="sq-text-center">Discount</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {bulkPricing.map((tier, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <span className="sq-tier-badge">T{tier.tier}</span>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="input-control sq-input-bulk"
                                      value={tier.minQty}
                                      onChange={(e) => updateBulkPricing(item.id, idx, 'minQty', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="input-control sq-input-bulk"
                                      value={tier.maxQty === 'Above' ? 'Above' : (tier.maxQty || '')}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        updateBulkPricing(item.id, idx, 'maxQty', val);
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="input-control sq-input-bulk"
                                      value={tier.unitPrice}
                                      onChange={(e) => updateBulkPricing(item.id, idx, 'unitPrice', e.target.value)}
                                    />
                                  </td>
                                  <td className="sq-text-center">
                                    <input
                                      type="text"
                                      className="input-control sq-input-bulk-center"
                                      value={tier.discount === 'Base' ? 'Base' : (tier.discount || '')}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        updateBulkPricing(item.id, idx, 'discount', val);
                                      }}
                                      placeholder="Base or %"
                                    />
                                  </td>
                                  <td>
                                    <button
                                      className="sq-btn-remove"
                                      onClick={() => removeBulkPricingTier(item.id, idx)}
                                    >
                                      <CloseOutlined />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="sq-bulk-pricing-actions">
                          <button
                            className={`btn-add-tier ${bulkPricing.length >= 3 ? 'sq-btn-disabled' : ''}`}
                            onClick={() => addBulkPricingTier(item.id)}
                            disabled={bulkPricing.length >= 3}
                          >
                            {bulkPricing.length >= 3 ? 'Maximum 3 Tiers' : '+ Add Tier'}
                          </button>
                          <label className="sq-checkbox-label sq-price-negotiable-label">
                            <input
                              type="checkbox"
                              checked={item.priceNegotiableAboveTier3 || false}
                              onChange={(e) => {
                                setQuotation(prev => ({
                                  ...prev,
                                  items: prev.items.map(i => 
                                    i.id === item.id 
                                      ? { ...i, priceNegotiableAboveTier3: e.target.checked }
                                      : i
                                  )
                                }));
                              }}
                            />
                            <span className="sq-price-negotiable-text">
                              Is price negotiable if quantity ordered is above Tier 3 slab?
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Item Details & Compliance */}
            <div className="sq-section">
              <h3 className="sq-section-title">Item Details & Compliance Information</h3>
              <p className="sq-help-text">Provide HSN code, certifications, and other compliance details for each item</p>
              
              {/* Item Tabs */}
              <div className="sq-item-tabs">
                {quotation.items.map(item => (
                  <button
                    key={item.id}
                    className={`sq-item-tab ${activeItemTab === item.id ? 'active' : ''}`}
                    onClick={() => setActiveItemTab(item.id)}
                  >
                    <span className="sq-tab-number">{item.id}</span>
                    <span className="sq-tab-text">{item.description}</span>
                  </button>
                ))}
              </div>

              {/* Item Details Form */}
              {quotation.items
                .filter(item => activeItemTab === item.id)
                .map(item => (
                  <div key={item.id} className="sq-item-details">
                    <div className="sq-grid-3">
                      <div className="sq-field">
                        <label>HSN / SAC Code *</label>
                        <input
                          type="text"
                          className="input-control"
                          value={item.hsnCode}
                          onChange={(e) => handleQuotationChange(item.id, 'hsnCode', e.target.value)}
                        />
                        <span className="sq-helper-text">Required for GST and customs</span>
                      </div>
                      <div className="sq-field">
                        <label>Country of Origin *</label>
                        <select
                          className="input-control"
                          value={item.countryOfOrigin || ''}
                          onChange={(e) => handleQuotationChange(item.id, 'countryOfOrigin', e.target.value)}
                        >
                          <option value="" disabled>Select Country</option>
                          {countryOfOriginOptions.map((opt, idx) => (
                            <option key={`country-${opt.value}-${idx}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <span className="sq-helper-text">For export compliance</span>
                      </div>
                      <div className="sq-field">
                        <label>Warranty / Guarantee Period</label>
                        <div className="sq-flex-warranty">
                          <input
                            type="number"
                            className="input-control"
                            value={parseWarranty(item.warranty || '').value || ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const parsed = parseWarranty(item.warranty || '');
                              const newWarranty = formatWarranty(inputValue, parsed.unit);
                              handleQuotationChange(item.id, 'warranty', newWarranty);
                            }}
                            placeholder="12"
                            min="0"
                          />
                          <select
                            className="input-control"
                            value={parseWarranty(item.warranty || '').unit || ''}
                            onChange={(e) => {
                              const parsed = parseWarranty(item.warranty || '');
                              const newWarranty = formatWarranty(parsed.value, e.target.value);
                              handleQuotationChange(item.id, 'warranty', newWarranty);
                            }}
                          >
                            <option value="" disabled>Select Warranty Unit</option>
                            {Object.entries(WARRANTY_PERIOD_UNITS).map(([key, value]) => (
                              <option key={key} value={key}>{value}</option>
                            ))}
                          </select>
                        </div>
                        <span className="sq-helper-text">Standard procurement requirement</span>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="sq-field sq-full-width">
                      <label>Certifications / Standards Met</label>
                      <div className="sq-certifications">
                        {certificationsList.map((cert, idx) => (
                          <label key={`cert-checkbox-${item.id}-${cert}-${idx}`} className="sq-checkbox-label">
                            <input
                              type="checkbox"
                              checked={item.certifications?.includes(cert)}
                              onChange={() => handleCertificationToggle(item.id, cert)}
                            />
                            <span>{cert}</span>
                          </label>
                        ))}
                      </div>
                      {item.certifications && item.certifications.length > 0 && (
                        <div className="sq-selected-certs">
                          <span className="sq-selected-label">Selected:</span>
                          {item.certifications.map((cert, idx) => (
                            <span key={`cert-tag-${item.id}-${cert}-${idx}`} className="sq-cert-tag">{cert}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sample Details */}
                    <div className="sq-field sq-full-width">
                      <h4 className="sq-subsection-title">Sample Details for this Item</h4>
                      <div className="sq-grid-6">
                        <div className="sq-field">
                          <label>Sample Offered</label>
                          <select
                            className="input-control"
                            value={item.sampleOffered}
                            onChange={(e) => handleQuotationChange(item.id, 'sampleOffered', e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </div>
                        <div className="sq-field">
                          <label>Sample Type</label>
                          <select
                            className="input-control"
                            value={item.sampleType}
                            onChange={(e) => handleQuotationChange(item.id, 'sampleType', e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Product">Product</option>
                            <option value="Prototype">Prototype</option>
                            <option value="Catalog">Catalog</option>
                          </select>
                        </div>
                        <div className="sq-field sq-sample-quantity-unit">
                          <label>Sample Quantity & Unit</label>
                          <div className="input-group-prefix-suffix input-group-row">
                            <input
                              type="number"
                              className="input-middle input-middle-with-border"
                              value={item.sampleQuantity || ''}
                              onChange={(e) => handleQuotationChange(item.id, 'sampleQuantity', e.target.value)}
                              placeholder="1"
                              min="0"
                            />
                            <div className="input-prefix input-prefix-no-borders">
                              <select
                                value={item.sampleUnit || ''}
                                onChange={(e) => handleQuotationChange(item.id, 'sampleUnit', e.target.value)}
                                disabled={uomLoading}
                              >
                                <option value="">
                                  {uomLoading ? 'Loading...' : 'Select Unit'}
                                </option>
                                {attributeUnitOptions && attributeUnitOptions.length > 0 && (
                                  attributeUnitOptions.map((opt, idx) => (
                                    <option key={`sample-uom-${opt.value}-${idx}`} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))
                                )}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="sq-field">
                          <label>Sample Cost ({getCurrencySymbol()})</label>
                          <input
                            type="number"
                            className="input-control"
                            value={item.sampleCost}
                            onChange={(e) => handleQuotationChange(item.id, 'sampleCost', e.target.value)}
                          />
                        </div>
                        <div className="sq-field">
                          <label>Dispatch Lead Time (Days)</label>
                          <input
                            type="number"
                            className="input-control"
                            value={item.dispatchLeadTime}
                            onChange={(e) => handleQuotationChange(item.id, 'dispatchLeadTime', e.target.value)}
                          />
                        </div>
                        <div className="sq-field">
                          <label>Return Policy</label>
                          <select
                            className="input-control"
                            value={item.returnPolicy}
                            onChange={(e) => handleQuotationChange(item.id, 'returnPolicy', e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Returnable">Returnable</option>
                            <option value="Non-Returnable">Non-Returnable</option>
                            <option value="On Approval Basis">On Approval Basis</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Supplier Declaration */}
            <div className="sq-section">
              <h3 className="sq-section-title">Supplier Declaration</h3>
              <div className="sq-declaration">
                <div className="sq-declaration-text">
                  <p>
                    I hereby declare that all information provided in this quotation is accurate and complete. The prices quoted are valid for the specified validity period. We agree to the terms and conditions of the RFQ and confirm our capability to deliver as per specifications.
                  </p>
                </div>
                <label className="sq-checkbox-label">
                  <input
                    type="checkbox"
                    checked={quotation.declarationAccepted}
                    onChange={(e) => setQuotation(prev => ({ ...prev, declarationAccepted: e.target.checked }))}
                  />
                  <span className="sq-declaration-text-label">I accept the terms and conditions and confirm the accuracy of information provided</span>
                </label>
              </div>
            </div>

            {/* Buyer-Seller Clarification Thread */}
            <div className="sq-section">
              <div className="sq-clarification-header" onClick={() => setClarificationThreadExpanded(!clarificationThreadExpanded)}>
                <div className="sq-clarification-header-left">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="sq-clarification-icon"
                    aria-hidden="true"
                  >
                    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <h3 className="sq-section-title">Buyer-Seller Clarification Thread</h3>
                </div>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                className={`sq-clarification-chevron ${clarificationThreadExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
                >
                  <path d="m18 15-6-6-6 6"></path>
                </svg>
              </div>
              {clarificationThreadExpanded && (
                <>
                  {!quotationDbId && (
                    <div className="sq-info-box" style={{ marginBottom: '16px', padding: '12px' }}>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ marginRight: '8px' }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 16v-4"></path>
                        <path d="M12 8h.01"></path>
                      </svg>
                      <span>Please save the quotation (as draft or submit) to enable messaging.</span>
                    </div>
                  )}
                  
                  {loadingConversations ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <span>Loading messages...</span>
                    </div>
                  ) : (
                  <div className="sq-messages">
                      {messages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          {quotationDbId 
                            ? 'No messages yet. Start the conversation!' 
                            : 'Save the quotation to start messaging.'}
                        </div>
                      ) : (
                        messages.map(msg => (
                      <div key={msg.id} className={`sq-message sq-message-${msg.sender}`}>
                            <span className={`sq-message-badge sq-badge-${msg.sender}`}>
                              {msg.sender === 'buyer' ? 'Buyer' : 'Seller'}
                            </span>
                        <div className="sq-message-content">
                          <div className="sq-message-text">{msg.text}</div>
                          <div className="sq-message-time">{msg.timestamp}</div>
                        </div>
                      </div>
                        ))
                      )}
                  </div>
                  )}
                  
                  <div className="sq-message-input">
                    <input
                      type="text"
                      className="sq-message-input-field"
                      placeholder={quotationDbId ? "Type your message here..." : "Save quotation first to send messages"}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !sendingMessage && quotationDbId) {
                          sendMessage();
                        }
                      }}
                      disabled={!quotationDbId || sendingMessage}
                    />
                    <button 
                      className="sq-send-btn" 
                      onClick={sendMessage}
                      disabled={!quotationDbId || sendingMessage || !newMessage.trim()}
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Revision History */}
            <div className="sq-section">
              <div className="sq-revision-header" onClick={() => setRevisionHistoryExpanded(!revisionHistoryExpanded)}>
                <div className="sq-revision-header-left">
                  <ClockCircleOutlined className="sq-revision-icon" />
                  <h3 className="sq-section-title">Revision History</h3>
                </div>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                className={`sq-revision-chevron ${revisionHistoryExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}
                aria-hidden="true"
                >
                  <path d="m18 15-6-6-6 6"></path>
                </svg>
              </div>
              {revisionHistoryExpanded && (
                <div className="sq-revision-content">
                  <div className="sq-revision-item">
                    <div className="sq-revision-header-info">
                      <span className="sq-revision-badge">Rev 2</span>
                      <span className="sq-revision-description">Updated pricing for Tier 2 and 3</span>
                    </div>
                    <div className="sq-revision-meta">Updated by: John Seller 2025-11-02 14:30</div>
                  </div>
                  <div className="sq-revision-item">
                    <div className="sq-revision-header-info">
                      <span className="sq-revision-badge">Rev 1</span>
                      <span className="sq-revision-description">Initial quote submission</span>
                    </div>
                    <div className="sq-revision-meta">Updated by: John Seller 2025-11-01 10:15</div>
                  </div>
                </div>
              )}
            </div>

            {/* Attachments */}
            <div className="sq-section">
              <h3 className="sq-section-title">Attachments</h3>
              <p className="sq-help-text">Upload supporting documents, certifications, or product images</p>
              
              <div className="sq-upload-area">
                <UploadOutlined className="sq-upload-icon" />
                <p className="sq-upload-text">Upload supporting documents, certifications, or product images</p>
                <p className="sq-upload-formats">Supported formats: PDF, DOC, JPG, PNG (Max 10MB)</p>
                <input
                  type="file"
                  id="file-upload"
                  className="sq-file-input"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="file-upload" className="btn-secondary sq-choose-files">
                  Choose Files
                </label>
              </div>

              {attachedFiles.length > 0 && (
                <div className="sq-attached-files">
                  {attachedFiles.map((file, idx) => (
                    <div key={idx} className="sq-file-item">
                      <span>{file.name}</span>
                      <button className="sq-btn-remove" onClick={() => removeFile(idx)}>
                        <CloseOutlined />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </div>

        {/* Footer Actions */}
        <div className="sq-footer">
          <div className="sq-footer-left">
            <span className="sq-last-saved">Last saved 2 minutes ago</span>
          </div>
        </div>
      </div>
    </div>
  );
}



